import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ChangeEvent } from "react";
import { Check, ChevronDown, Copy, Download, ExternalLink, FileText, FileUp, Flag, FolderOpen, Image as ImageIcon, Languages, ListChecks, Minus, Pilcrow, Plus, Quote, Redo2, Save, Search, Share2, Smartphone, Tags, Trash2, Undo2, X } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Document, Page, pdfjs } from "react-pdf";
import {
  buildMobileZip,
  buildMobileAiReviewPack,
  buildMobileCodexRecoveryPack,
  checklistMobileQa,
  currentMobileQa,
  decryptMobileBundle,
  decryptMobilePatch,
  encryptMobilePatch,
  extractPortableDocxPreview,
  extractPortableOdtPreview,
  extractPortableReferenceText,
  extractPortableWorkbookGrid,
  importMobileJobFiles,
  estimateMobileBundleStorageBytes,
  getMobileStorageStatus,
  isMobileQuotaError,
  loadBundledMobileBundle,
  loadImportedMobileBundle,
  loadMobileActiveJobPreference,
  loadMobilePatch,
  mobileCompletion,
  normalizeVivoOsApostrophes,
  portableLayoutPreview,
  renderMobileXbenchReport,
  requestMobileStoragePersistence,
  searchMobileTm,
  saveBundledMobileBundle,
  saveImportedMobileBundle,
  saveMobileActiveJobPreference,
  saveMobilePatch,
  type MobileEnvelope,
  type MobileJobBundle,
  type MobileJobCatalogEntry,
  type MobilePatch,
  type MobileQaIssue,
  type MobileReferenceFile,
  type MobileSegment,
  type MobileStorageStatus,
  type PortableWorkbookGrid,
  type PortableDocxPreview,
} from "./mobile";
import { countSourceWords } from "./editorMetrics";
import "./mobile.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

type CompanionTab = "terms" | "visuals" | "qa";
type MobileSheet = "menu" | "jobs" | "browse" | "companion" | null;
type QuickCategory = "files" | "resources" | "editor" | "delivery";
type ApostropheMode = "auto" | "curly" | "straight";
type TargetSnapshot = { target: string; reviewStatus: string };
type TargetHistory = { past: TargetSnapshot[]; future: TargetSnapshot[] };
type MobileInstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };
type ReferencePreview = {
  reference: MobileReferenceFile;
  kind: "pdf" | "text" | "workbook" | "docx" | "formatted";
  viewMode?: "native" | "layout";
  query: string;
  url?: string;
  layoutUrl?: string;
  text?: string;
  workbook?: PortableWorkbookGrid;
  docx?: PortableDocxPreview;
  focusSheet?: string | null;
  focusRow?: number | null;
};

const MOBILE_SPECIAL_CHARACTERS = [
  { glyph: "·", label: "NBSP", value: "\u00a0", title: "Non-breaking space" },
  { glyph: "·", label: "NNBSP", value: "\u202f", title: "Narrow non-breaking space" },
  { glyph: "‑", label: "NB hyphen", value: "\u2011", title: "Non-breaking hyphen" },
  { glyph: "’", label: "Curly ’", value: "’", title: "Curly apostrophe" },
  { glyph: "«", label: "Open «", value: "«", title: "Opening French guillemet" },
  { glyph: "»", label: "Close »", value: "»", title: "Closing French guillemet" },
  { glyph: "…", label: "Ellipsis", value: "…", title: "Ellipsis" },
  { glyph: "–", label: "En dash", value: "–", title: "En dash" },
  { glyph: "—", label: "Em dash", value: "—", title: "Em dash" },
  { glyph: "×", label: "Multiply", value: "×", title: "Multiplication sign" },
  { glyph: "°", label: "Degree", value: "°", title: "Degree sign" },
  { glyph: "↵", label: "Line break", value: "\n", title: "Line break" },
] as const;
const QUICK_CATEGORIES: QuickCategory[] = ["files", "resources", "editor", "delivery"];
const IMPORTANT_MOBILE_MESSAGE = /failed|unavailable|could not|not found|lost|export .*now|larger than|safety limit|not valid|not readable|damaged|password-protected|multiple mobile-job|skipped|storage|home screen/i;
const MOBILE_EDIT_HOLD_MS = 420;

function mobileRunsStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function MobileWorkspace({ jobId, staticBundleUrl, catalogUrl }: { jobId: string; staticBundleUrl?: string; catalogUrl?: string }) {
  const [activeBundleUrl, setActiveBundleUrl] = useState(staticBundleUrl);
  const [bundle, setBundle] = useState<MobileJobBundle | null>(null);
  const [segments, setSegments] = useState<MobileSegment[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [intakeAttempted, setIntakeAttempted] = useState(false);
  const [message, setMessage] = useState(activeBundleUrl ? "Opening served job…" : "Opening encrypted job…");
  const [noticeVisible, setNoticeVisible] = useState(true);
  const [noticeExpanded, setNoticeExpanded] = useState(false);
  const [sheet, setSheet] = useState<MobileSheet>(null);
  const [quickCategory, setQuickCategory] = useState<QuickCategory>("files");
  const [specialCharactersOpen, setSpecialCharactersOpen] = useState(false);
  const [apostropheMode, setApostropheMode] = useState<ApostropheMode>(() => (safeLocalGet("tcc-mobile-apostrophe") as ApostropheMode) || "auto");
  const [tmOpen, setTmOpen] = useState(false);
  const [tmQuery, setTmQuery] = useState("");
  const [gesturePreview, setGesturePreview] = useState<"visuals" | "confirm" | null>(null);
  const [companionTab, setCompanionTab] = useState<CompanionTab>("terms");
  const [fontSize, setFontSize] = useState(() => Number(safeLocalGet("tcc-mobile-font")) || 16);
  const [browseQuery, setBrowseQuery] = useState("");
  const [browseTargetQuery, setBrowseTargetQuery] = useState("");
  const [browseTargetNot, setBrowseTargetNot] = useState(false);
  const [jobCatalog, setJobCatalog] = useState<MobileJobCatalogEntry[]>([]);
  const [referencePreview, setReferencePreview] = useState<ReferencePreview | null>(null);
  const [standalone, setStandalone] = useState(() => mobileRunsStandalone());
  const [localAuthority, setLocalAuthority] = useState(() => safeLocalGet("tcc-mobile-active-job") === "imported");
  const [servedJobAvailable, setServedJobAvailable] = useState(false);
  const [storageStatus, setStorageStatus] = useState<MobileStorageStatus | null>(null);
  const [editorFocusRequest, setEditorFocusRequest] = useState(0);
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null);
  const saveTimer = useRef<number | null>(null);
  const segmentsRef = useRef<MobileSegment[]>([]);
  const segmentScrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const resourceInputRef = useRef<HTMLInputElement | null>(null);
  const referenceInputRef = useRef<HTMLInputElement | null>(null);
  const modalFocusReturn = useRef<HTMLElement | null>(null);
  const referenceFocusReturn = useRef<HTMLElement | null>(null);
  const rowGesture = useRef<{ segmentId: string; startX: number; startY: number; latestX: number; latestY: number; startedAt: number; canSwipe: boolean; pane: "visuals" | "confirm" | null; holdTimer: number | null; held: boolean; targetEditor: HTMLElement | null } | null>(null);
  const targetHistory = useRef(new Map<string, TargetHistory>());
  const companionDismissX = useRef<number | null>(null);
  const pendingEditorFocus = useRef<{ segmentId: string; position: number } | null>(null);
  const installPrompt = useRef<MobileInstallPrompt | null>(null);
  const importController = useRef<AbortController | null>(null);
  const mobileApi = new URLSearchParams(window.location.search).get("mobileApi");
  const passphrase = new URLSearchParams(window.location.hash.slice(1)).get("key") ?? "";
  // A raw phone import is also a local-authority job even when the app was
  // opened without a served `job=` URL.
  const staticMode = Boolean(activeBundleUrl) || localAuthority;

  useEffect(() => {
    safeLocalSet("tcc-mobile-font", String(fontSize));
  }, [fontSize]);

  useEffect(() => {
    safeLocalSet("tcc-mobile-apostrophe", apostropheMode);
  }, [apostropheMode]);

  useEffect(() => {
    setEditingSegmentId(null);
    targetHistory.current.clear();
  }, [bundle?.project.id]);

  useEffect(() => {
    let cancelled = false;
    const required = bundle ? estimateMobileBundleStorageBytes(bundle) : 0;
    void getMobileStorageStatus(required).then((status) => { if (!cancelled) setStorageStatus(status); });
    return () => { cancelled = true; };
  }, [bundle]);

  useEffect(() => {
    const rememberPrompt = (event: Event) => {
      event.preventDefault();
      installPrompt.current = event as MobileInstallPrompt;
    };
    const installed = () => {
      installPrompt.current = null;
      setStandalone(true);
      setMessage("Full-screen phone app installed.");
    };
    window.addEventListener("beforeinstallprompt", rememberPrompt);
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", rememberPrompt);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  useEffect(() => {
    if (!catalogUrl) return;
    let cancelled = false;
    void fetch(catalogUrl, { cache: "no-store" }).then(async (response) => {
      if (!response.ok) throw new Error(`Job catalog is unavailable (${response.status}).`);
      const value = await response.json() as { version?: number; jobs?: MobileJobCatalogEntry[] } | MobileJobCatalogEntry[];
      const jobs = Array.isArray(value) ? value : value.jobs;
      if (!Array.isArray(jobs)) throw new Error("Job catalog has an invalid format.");
      if (!cancelled) setJobCatalog(jobs);
    }).catch(() => { if (!cancelled) setJobCatalog([]); });
    return () => { cancelled = true; };
  }, [catalogUrl]);

  useEffect(() => () => {
    if (referencePreview?.url) URL.revokeObjectURL(referencePreview.url);
    if (referencePreview?.layoutUrl) URL.revokeObjectURL(referencePreview.layoutUrl);
  }, [referencePreview?.url, referencePreview?.layoutUrl]);

  useEffect(() => {
    setNoticeVisible(true);
    setNoticeExpanded(false);
    if (busy) return;
    const important = IMPORTANT_MOBILE_MESSAGE.test(message);
    const timer = window.setTimeout(() => setNoticeVisible(false), important ? 9000 : 3600);
    return () => window.clearTimeout(timer);
  }, [message, busy]);

  useEffect(() => {
    if (!sheet || sheet === "menu") return;
    modalFocusReturn.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const background = [...document.querySelectorAll<HTMLElement>(".mobile-header,.mobile-top-pane,.mobile-segment-scroll,.mobile-announcement,.mobile-status-bar")];
    for (const item of background) {
      item.inert = true;
      item.setAttribute("aria-hidden", "true");
    }
    const focusTimer = window.setTimeout(() => {
      const preferred = document.querySelector<HTMLElement>(sheet === "browse" ? ".mobile-search input" : ".mobile-sheet-header button");
      preferred?.focus({ preventScroll: true });
    }, 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (document.querySelector(".mobile-reference-preview")) return;
      if (event.key === "Escape") setSheet(null);
      if (event.key !== "Tab") return;
      const focusable = [...document.querySelectorAll<HTMLElement>('.mobile-sheet button:not(:disabled),.mobile-sheet input:not(:disabled),.mobile-sheet summary,[href]')].filter((item) => item.getBoundingClientRect().width > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1)!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      for (const item of background) {
        item.inert = false;
        item.removeAttribute("aria-hidden");
      }
      // Selecting a search result deliberately transfers focus into the
      // target editor. Do not let dialog cleanup steal it back for the button
      // that opened the sheet.
      if (!pendingEditorFocus.current) modalFocusReturn.current?.focus({ preventScroll: true });
    };
  }, [sheet]);

  useEffect(() => {
    if (!referencePreview) return;
    referenceFocusReturn.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const companionBackdrop = document.querySelector<HTMLElement>(".mobile-sheet-backdrop");
    if (companionBackdrop) {
      companionBackdrop.inert = true;
      companionBackdrop.setAttribute("aria-hidden", "true");
    }
    const focusTimer = window.setTimeout(() => document.querySelector<HTMLElement>(".mobile-reference-preview [aria-label='Close reference preview']")?.focus({ preventScroll: true }), 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); setReferencePreview(null); return; }
      if (event.key !== "Tab") return;
      const focusable = [...document.querySelectorAll<HTMLElement>('.mobile-reference-preview button:not(:disabled),.mobile-reference-preview [href],.mobile-reference-preview [tabindex]:not([tabindex="-1"])')].filter((item) => item.getBoundingClientRect().width > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1)!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      if (companionBackdrop) {
        companionBackdrop.inert = false;
        companionBackdrop.removeAttribute("aria-hidden");
      }
      referenceFocusReturn.current?.focus({ preventScroll: true });
    };
  }, [referencePreview]);

  useEffect(() => {
    const viewport = window.visualViewport;
    let widest = viewport?.width ?? window.innerWidth;
    let baselineHeight = viewport?.height ?? window.innerHeight;
    const update = () => {
      const height = viewport?.height ?? window.innerHeight;
      const width = viewport?.width ?? window.innerWidth;
      if (Math.abs(width - widest) > 72) {
        widest = width;
        baselineHeight = height;
      } else baselineHeight = Math.max(baselineHeight, height);
      const keyboardOpen = Boolean(viewport && height < baselineHeight - 96);
      document.documentElement.classList.toggle("mobile-keyboard-open", keyboardOpen);
      document.documentElement.style.setProperty("--mobile-viewport-height", `${height}px`);
      document.documentElement.style.setProperty("--mobile-viewport-width", `${width}px`);
      document.documentElement.style.setProperty("--mobile-viewport-top", `${viewport?.offsetTop ?? 0}px`);
      document.documentElement.style.setProperty("--mobile-viewport-left", `${viewport?.offsetLeft ?? 0}px`);
    };
    update();
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    window.addEventListener("orientationchange", update);
    return () => {
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
      window.removeEventListener("orientationchange", update);
      document.documentElement.classList.remove("mobile-keyboard-open");
    };
  }, []);

  async function activatePlainBundle(result: MobileJobBundle, readyMessage: string, preferredSelectedId?: string | null) {
    const localPatch = safeLocalGet(`tcc-mobile-static-patch:${result.project.id}`);
    const durablePatch = await loadMobilePatch(`static:${result.project.id}`).catch(() => null);
    let initial = result.segments;
    const checkpoints: MobilePatch[] = durablePatch ? [durablePatch] : [];
    if (localPatch) try { checkpoints.push(JSON.parse(localPatch) as MobilePatch); } catch { /* Ignore an invalid synchronous fallback. */ }
    const cachedPatch = checkpoints
      .filter((item) => item.project_id === result.project.id && item.source_package_hash === result.source_package_hash)
      .sort((left, right) => right.submitted_at.localeCompare(left.submitted_at))[0];
    if (cachedPatch) {
      const byId = new Map(cachedPatch.changes.map((item) => [item.segment_id, item]));
      initial = result.segments.map((item) => {
        const saved = byId.get(item.id);
        return saved && saved.source_hash === item.source_hash
          ? { ...item, target: saved.target, review_status: saved.review_status }
          : item;
      });
    }
    setBundle(result);
    setEditingSegmentId(null);
    targetHistory.current.clear();
    segmentsRef.current = initial;
    setSegments(initial);
    const preserved = preferredSelectedId && initial.some((item) => item.id === preferredSelectedId) ? preferredSelectedId : null;
    setSelectedId(preserved ?? initial.find((item) => !item.locked && !isConfirmed(item.review_status))?.id ?? initial.find((item) => !item.locked)?.id ?? null);
    setMessage(readyMessage);
  }

  useEffect(() => {
    let cancelled = false;
    async function open() {
      const activePreference = safeLocalGet("tcc-mobile-active-job") || await loadMobileActiveJobPreference().catch(() => null);
      if (activePreference === "imported") {
        const imported = await loadImportedMobileBundle().catch(() => null);
        if (imported && !cancelled) {
          if (activeBundleUrl) {
            const servedFallback = await loadBundledMobileBundle(activeBundleUrl).catch(() => null);
            if (!cancelled) setServedJobAvailable(Boolean(servedFallback));
          }
          setLocalAuthority(true);
          await activatePlainBundle(imported, "Imported job restored. Edits are saved automatically on this phone.");
          return;
        }
        safeLocalRemove("tcc-mobile-active-job");
        setLocalAuthority(false);
        void saveMobileActiveJobPreference("served").catch(() => { /* The stale marker is already cleared locally. */ });
      }
      if (activeBundleUrl) {
        setServedJobAvailable(false);
        let result: MobileJobBundle;
        let restoredOffline = false;
        let phoneAuthorityPreserved = false;
        const cachedBundle = await loadBundledMobileBundle(activeBundleUrl).catch(() => null);
        try {
          const response = await fetch(activeBundleUrl, { cache: "no-store" });
          if (!response.ok) throw new Error(`Standalone job is unavailable (${response.status}).`);
          const served = await response.json() as MobileJobBundle;
          const cachedPatch = cachedBundle
            ? safeLocalGet(`tcc-mobile-static-patch:${cachedBundle.project.id}`) || await loadMobilePatch(`static:${cachedBundle.project.id}`).catch(() => null)
            : null;
          if (cachedBundle && cachedPatch && cachedBundle.project.id === served.project.id && cachedBundle.source_package_hash !== served.source_package_hash) {
            result = cachedBundle;
            phoneAuthorityPreserved = true;
          } else {
            result = served;
            void saveBundledMobileBundle(result, activeBundleUrl).catch(() => { /* Safari private mode may disable durable storage. */ });
          }
        } catch (error) {
          if (!cachedBundle) throw error;
          result = cachedBundle;
          restoredOffline = true;
        }
        if (result.version !== 1 || result.kind !== "translation-command-center-mobile-job") {
          throw new Error("The bundled mobile job has an unsupported format.");
        }
        if (cancelled) return;
        setServedJobAvailable(true);
        await activatePlainBundle(result, phoneAuthorityPreserved
          ? "Phone-authority branch preserved. The served snapshot changed, but this phone can still export its final bilingual independently."
          : restoredOffline || !navigator.onLine ? "Offline served job restored. Edits still save on this phone." : "Served job ready. This phone branch is the final authority.");
        return;
      }
      if (!passphrase) throw new Error("The mobile link has no encryption key.");
      const cached = safeLocalGet(`tcc-mobile-envelope:${jobId}`);
      let envelope: MobileEnvelope;
      try {
        const url = mobileApi
          ? `${mobileApi}?resource=job&id=${encodeURIComponent(jobId)}`
          : `/api/mobile/jobs/${encodeURIComponent(jobId)}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Mobile job is unavailable (${response.status}).`);
        envelope = await response.json() as MobileEnvelope;
        safeLocalSet(`tcc-mobile-envelope:${jobId}`, JSON.stringify(envelope));
      } catch (error) {
        if (!cached) throw error;
        envelope = JSON.parse(cached) as MobileEnvelope;
        setMessage("Offline copy opened. Changes will remain encrypted on this phone until the connection returns.");
      }
      const result = await decryptMobileBundle(envelope, passphrase);
      if (cancelled) return;
      const localPatch = safeLocalGet(`tcc-mobile-patch:${jobId}`);
      let initial = result.segments;
      if (localPatch) {
        try {
          const cachedPatch = await decryptMobilePatch(JSON.parse(localPatch) as MobileEnvelope, passphrase);
          if (cachedPatch.project_id === result.project.id && cachedPatch.source_package_hash === result.source_package_hash) {
            const byId = new Map(cachedPatch.changes.map((item) => [item.segment_id, item]));
            initial = result.segments.map((item) => {
              const saved = byId.get(item.id);
              return saved && saved.source_hash === item.source_hash
                ? { ...item, target: saved.target, review_status: saved.review_status }
                : item;
            });
          }
        } catch { /* Ignore an invalid or obsolete encrypted phone cache. */ }
      }
      setBundle(result);
      segmentsRef.current = initial;
      setSegments(initial);
      setSelectedId(initial.find((item) => !item.locked && !isConfirmed(item.review_status))?.id ?? initial.find((item) => !item.locked)?.id ?? null);
      setMessage("Encrypted job ready.");
    }
    void open().catch((error) => setMessage(error instanceof Error ? error.message : "Mobile job could not be opened.")).finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [activeBundleUrl, jobId, mobileApi, passphrase]);

  const qa = useMemo(() => {
    if (!bundle) return { issues: [] as MobileQaIssue[], unsupported: [] as string[] };
    const base = currentMobileQa(segments);
    const checklist = checklistMobileQa(bundle, segments);
    return { issues: [...base, ...checklist.issues], unsupported: checklist.unsupported };
  }, [bundle, segments]);
  const completion = useMemo(() => mobileCompletion(segments, qa.issues), [segments, qa.issues]);
  const selected = segments.find((item) => item.id === selectedId) ?? null;
  const companions = selected && bundle ? bundle.companions[selected.id] : null;
  const resolvedScreenshots = useMemo(() => {
    if (!companions?.screenshots.length) return [];
    const references = new Map((bundle?.reference_files ?? []).map((item) => [item.id, item]));
    return companions.screenshots.map((item) => {
      if (item.image_url || !item.reference_file_id) return item;
      const reference = references.get(item.reference_file_id);
      return reference ? { ...item, image_url: `data:${reference.mime_type};base64,${reference.content_base64}` } : item;
    });
  }, [bundle?.reference_files, companions?.screenshots]);
  const companionDocuments = companions?.documents ?? [];
  const jobReferenceFiles = (bundle?.reference_files ?? [])
    .filter((item) => !item.mime_type.startsWith("image/") && !item.generated_preview)
    .sort((left, right) => mobileReferencePriority(left) - mobileReferencePriority(right) || left.name.localeCompare(right.name));
  const referenceRow = companions?.reference_row ?? null;
  const browseResults = useMemo(() => {
    const sourceNeedle = browseQuery.trim().toLocaleLowerCase();
    const targetNeedle = browseTargetQuery.trim().toLocaleLowerCase();
    return segments.filter((item) => {
      const sourceMatch = !sourceNeedle || item.source_text.toLocaleLowerCase().includes(sourceNeedle);
      const rawTargetMatch = !targetNeedle || item.target.toLocaleLowerCase().includes(targetNeedle);
      const targetMatch = browseTargetNot && targetNeedle ? !rawTargetMatch : rawTargetMatch;
      return sourceMatch && targetMatch;
    });
  }, [segments, browseQuery, browseTargetQuery, browseTargetNot]);
  const tmResults = useMemo(() => bundle ? searchMobileTm(bundle, tmQuery || selected?.source_text || "") : [], [bundle, selected?.source_text, tmQuery]);
  const selectedIndex = selected ? segments.findIndex((item) => item.id === selected.id) : -1;
  const confirmedCount = segments.filter((item) => isConfirmed(item.review_status)).length;
  const sourceWordProgress = useMemo(() => {
    let confirmed = 0;
    let draft = 0;
    for (const item of segments) {
      if (item.locked) continue;
      const words = countSourceWords(item.source_text, bundle?.project.source_locale ?? "en-US");
      if (isConfirmed(item.review_status)) confirmed += words;
      else draft += words;
    }
    return { confirmed, draft, total: confirmed + draft };
  }, [bundle?.project.source_locale, segments]);
  const segmentVirtualizer = useVirtualizer({
    count: segments.length,
    getScrollElement: () => segmentScrollRef.current,
    estimateSize: () => 104,
    overscan: 7,
    getItemKey: (index) => segments[index]?.id ?? index,
  });

  useEffect(() => {
    const openFromKeyboard = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === "z" && editingSegmentId && selected) {
        event.preventDefault();
        restoreTargetHistory(selected, event.shiftKey ? "redo" : "undo");
        return;
      }
      if (event.key !== "F3") return;
      event.preventDefault();
      openTmSearch();
    };
    document.addEventListener("keydown", openFromKeyboard);
    return () => document.removeEventListener("keydown", openFromKeyboard);
  }, [editingSegmentId, selected]);

  function openTmSearch() {
    const selection = window.getSelection();
    const anchor = selection?.anchorNode instanceof Element ? selection.anchorNode : selection?.anchorNode?.parentElement;
    const selectedSource = anchor?.closest(".mobile-row-source") ? selection?.toString().trim() ?? "" : "";
    setTmQuery(selectedSource || selected?.source_text || "");
    setSheet(null);
    setTmOpen(true);
  }

  function openCatalogJob(entry: MobileJobCatalogEntry) {
    try {
      const resolved = new URL(entry.bundle_url, window.location.href);
      if (resolved.origin !== window.location.origin || !/\/jobs\/[^?#]+\/mobile-job\.json$/i.test(resolved.pathname)) throw new Error("The catalog entry points outside the TCC jobs folder.");
      setSheet(null);
      setTmOpen(false);
      setMessage(`Opening ${entry.client} · ${entry.identifier}…`);
      setNoticeVisible(true);
      setActiveBundleUrl(resolved.href);
      const url = new URL(window.location.href);
      url.searchParams.set("job", resolved.pathname);
      window.history.replaceState(null, "", url);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "This served job cannot be opened.");
    }
  }

  useEffect(() => {
    if (!bundle) return;
    const flush = () => void submitPatch(false);
    const flushWhenHidden = () => { if (document.visibilityState === "hidden") flush(); };
    const flushBeforeAppUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ waitUntil?: (work: Promise<unknown>) => void }>).detail;
      detail?.waitUntil?.(submitPatch(false));
    };
    const announceAppUpdate = () => setMessage("An app update is ready. This job will stay open; the update installs safely after you leave the tab.");
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flushWhenHidden);
    window.addEventListener("tcc-before-app-update", flushBeforeAppUpdate);
    window.addEventListener("tcc-app-update-ready", announceAppUpdate);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flushWhenHidden);
      window.removeEventListener("tcc-before-app-update", flushBeforeAppUpdate);
      window.removeEventListener("tcc-app-update-ready", announceAppUpdate);
    };
  }, [bundle, jobId, mobileApi, passphrase, staticMode]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    let timer = 0;
    const keepEditingRowVisible = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        document.querySelectorAll<HTMLElement>("[data-mobile-target]").forEach((editor) => resizeTargetField(editor));
        const active = document.activeElement as HTMLElement | null;
        const segmentId = active?.dataset.mobileTarget ?? selectedId;
        if (!segmentId) return;
        const index = segmentsRef.current.findIndex((item) => item.id === segmentId);
        if (index >= 0) {
          segmentVirtualizer.scrollToIndex(index, { align: "auto" });
          // A long bilingual row can be taller than the post-keyboard visual
          // viewport. Re-show the exact active chunk after the virtual row has
          // moved, rather than merely making some part of the row visible.
          window.requestAnimationFrame(() => {
            active?.scrollIntoView({ block: "nearest", inline: "nearest" });
            if (active) scrollMobileCaretIntoView(active);
          });
        }
      }, 80);
    };
    viewport.addEventListener("resize", keepEditingRowVisible);
    return () => {
      window.clearTimeout(timer);
      viewport.removeEventListener("resize", keepEditingRowVisible);
    };
  }, [fontSize, segmentVirtualizer, selectedId]);

  useEffect(() => {
    const pending = pendingEditorFocus.current;
    if (!pending) return;
    // A newer user selection always wins over any delayed virtual-row focus
    // request. Dropping stale work prevents a later render from unexpectedly
    // stealing the caret back to an older row.
    if (pending.segmentId !== selectedId) {
      pendingEditorFocus.current = null;
      return;
    }
    let cancelled = false;
    let attempts = 0;
    let timer = 0;
    const focus = () => {
      if (cancelled || pendingEditorFocus.current?.segmentId !== pending.segmentId) return;
      const editor = focusMobileTargetPosition(pending.segmentId, pending.position);
      if (editor) {
        resizeTargetField(editor);
        editor.scrollIntoView({ block: "nearest", inline: "nearest" });
        scrollMobileCaretIntoView(editor);
        pendingEditorFocus.current = null;
        return;
      }
      attempts += 1;
      if (attempts < 6) timer = window.setTimeout(focus, attempts * 35);
    };
    // React Virtual may need a layout/measurement pass after the selected row
    // changes, especially when the next draft is far away or is a long row.
    timer = window.setTimeout(focus, 35);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [editorFocusRequest, selectedId]);

  function updateSegment(segmentId: string, update: Partial<MobileSegment>, recordHistory = true) {
    const currentSegment = segmentsRef.current.find((item) => item.id === segmentId);
    if (recordHistory && currentSegment && update.target != null && update.target !== currentSegment.target) {
      const history = targetHistory.current.get(segmentId) ?? { past: [], future: [] };
      history.past.push({ target: currentSegment.target, reviewStatus: currentSegment.review_status });
      if (history.past.length > 100) history.past.splice(0, history.past.length - 100);
      history.future = [];
      targetHistory.current.set(segmentId, history);
    }
    setSegments((current) => {
      const next = current.map((item) => item.id === segmentId ? { ...item, ...update } : item);
      segmentsRef.current = next;
      return next;
    });
    schedulePatch();
  }

  function beginEditing(segment: MobileSegment, position = segment.target.length) {
    if (segment.locked) return;
    setSheet(null);
    setTmOpen(false);
    setSelectedId(segment.id);
    setEditingSegmentId(segment.id);
    pendingEditorFocus.current = { segmentId: segment.id, position };
    setEditorFocusRequest((value) => value + 1);
    const index = segmentsRef.current.findIndex((item) => item.id === segment.id);
    if (index >= 0) window.setTimeout(() => segmentVirtualizer.scrollToIndex(index, { align: "center" }), 0);
  }

  function finishEditing() {
    if (document.activeElement instanceof HTMLElement && document.activeElement.dataset.mobileTarget) document.activeElement.blur();
    pendingEditorFocus.current = null;
    setEditingSegmentId(null);
  }

  function restoreTargetHistory(segment: MobileSegment, direction: "undo" | "redo") {
    const history = targetHistory.current.get(segment.id);
    if (!history) return;
    const source = direction === "undo" ? history.past : history.future;
    const destination = direction === "undo" ? history.future : history.past;
    const snapshot = source.pop();
    if (!snapshot) return;
    destination.push({ target: segment.target, reviewStatus: segment.review_status });
    updateSegment(segment.id, { target: snapshot.target, review_status: snapshot.reviewStatus }, false);
    window.setTimeout(() => {
      const editor = focusMobileTargetPosition(segment.id, snapshot.target.length);
      resizeTargetField(editor);
    }, 0);
  }

  function schedulePatch() {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => void submitPatch(false), 1200);
  }

  async function submitPatch(showMessage = true) {
    if (!bundle) return;
    const state = segmentsRef.current;
    const stateQa = [...currentMobileQa(state), ...checklistMobileQa(bundle, state).issues];
    const patch: MobilePatch = {
      version: 1,
      project_id: bundle.project.id,
      source_package_hash: bundle.source_package_hash,
      submitted_at: new Date().toISOString(),
      complete: mobileCompletion(state, stateQa).complete,
      changes: state.map((item) => ({ segment_id: item.id, source_hash: item.source_hash, target: item.target, review_status: item.review_status })),
    };
    if (staticMode) {
      const serialized = JSON.stringify(patch);
      // Keep only small synchronous pagehide checkpoints in localStorage. The
      // durable copy below lives in IndexedDB and is not constrained by the
      // much smaller localStorage ceiling.
      const storedSynchronously = serialized.length <= 240_000
        ? safeLocalSet(`tcc-mobile-static-patch:${bundle.project.id}`, serialized)
        : false;
      let storedDurably = false;
      try {
        await saveMobilePatch(patch, `static:${bundle.project.id}`);
        storedDurably = true;
      } catch { /* Report both stores below. */ }
      if (!storedDurably && !storedSynchronously) {
        setMessage("Phone browser storage is full or unavailable. Export a draft ZIP now so these edits are not lost.");
        return;
      }
      if (showMessage) setMessage(storedDurably
        ? "Saved durably on this phone. Export a ZIP whenever you want the final bilingual."
        : "Saved in the emergency tab cache only. Export a draft ZIP before closing Safari.");
      return;
    }
    if (!passphrase) return;
    const envelope = await encryptMobilePatch(patch, passphrase);
    const storedLocally = safeLocalSet(`tcc-mobile-patch:${jobId}`, JSON.stringify(envelope));
    if (!storedLocally) setMessage("Phone browser storage is full or unavailable. Trying the secure sync now; export a draft ZIP if it fails.");
    try {
      const url = mobileApi
        ? `${mobileApi}?resource=patch&id=${encodeURIComponent(jobId)}`
        : `/api/mobile/jobs/${encodeURIComponent(jobId)}/patch`;
      const response = await fetch(url, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(envelope) });
      if (!response.ok) throw new Error(`Sync failed (${response.status}).`);
      if (showMessage) setMessage("Encrypted review saved for Codex and the Mac.");
    } catch {
      setMessage(storedLocally ? "Saved encrypted on this phone. It will sync when the mobile host is reachable." : "Secure sync failed and this browser cannot retain a local copy. Export a draft ZIP now so these edits are not lost.");
    }
  }

  function confirm(segment: MobileSegment) {
    if (!segment.target.trim()) { setMessage("Enter a target before confirming this segment."); return; }
    updateSegment(segment.id, { review_status: "validated" });
    finishEditing();
    const later = segments.slice(selectedIndex + 1).find((item) => !item.locked && !isConfirmed(item.review_status));
    const wrapped = segments.slice(0, Math.max(0, selectedIndex)).find((item) => !item.locked && !isConfirmed(item.review_status));
    const next = later ?? wrapped;
    if (next) {
      setSelectedId(next.id);
      const nextIndex = segments.findIndex((item) => item.id === next.id);
      window.setTimeout(() => {
        segmentVirtualizer.scrollToIndex(nextIndex, { align: "center" });
      }, 0);
      setMessage(`Confirmed. Moved to the next draft: segment ${next.ordinal}.`);
    } else setMessage("Confirmed. No draft segment remains.");
  }

  function moveSelection(delta: number) {
    if (!segments.length) return;
    const index = Math.min(segments.length - 1, Math.max(0, selectedIndex + delta));
    finishEditing();
    setSelectedId(segments[index].id);
    segmentVirtualizer.scrollToIndex(index, { align: "center" });
  }

  function chooseSegment(segment: MobileSegment) {
    finishEditing();
    setSelectedId(segment.id);
    setSheet(null);
    const index = segments.findIndex((item) => item.id === segment.id);
    window.setTimeout(() => {
      segmentVirtualizer.scrollToIndex(index, { align: isMobileLongSegment(segment) ? "start" : "center" });
    }, 0);
  }

  function resizeTargetField(editor: HTMLElement | null) {
    resizeMobileTargetField(editor);
  }

  function insertIntoTarget(segment: MobileSegment, text: string) {
    if (!text || segment.locked) return;
    const editor = activeMobileTargetEditor(segment.id);
    const selection = editor ? mobileEditorSelection(editor) : null;
    const editorOffset = Number(editor?.dataset.mobileOffset ?? 0);
    const start = selection ? editorOffset + selection.start : segment.target.length;
    const end = selection ? editorOffset + selection.end : start;
    const target = `${segment.target.slice(0, start)}${text}${segment.target.slice(end)}`;
    updateSegment(segment.id, { target, review_status: "pending" });
    if (editingSegmentId !== segment.id) beginEditing({ ...segment, target, review_status: "pending" }, start + text.length);
    window.setTimeout(() => {
      const current = focusMobileTargetPosition(segment.id, start + text.length);
      resizeTargetField(current);
    }, 0);
  }

  function insertSourceTag(segment: MobileSegment, tag: string) {
    const editor = activeMobileTargetEditor(segment.id);
    const selection = editor ? mobileEditorSelection(editor) : null;
    const editorOffset = Number(editor?.dataset.mobileOffset ?? 0);
    const closing = tag.match(/^⟦T(\d+)⟧$/)?.[1];
    const closingTag = closing == null ? "" : `⟦/T${closing}⟧`;
    if (selection && selection.start !== selection.end && closingTag && segment.source_text.includes(closingTag)) {
      const start = editorOffset + selection.start;
      const end = editorOffset + selection.end;
      const target = `${segment.target.slice(0, start)}${tag}${segment.target.slice(start, end)}${closingTag}${segment.target.slice(end)}`;
      updateSegment(segment.id, { target, review_status: "pending" });
      window.setTimeout(() => {
        const current = focusMobileTargetPosition(segment.id, start + tag.length);
        if (current) {
          const offset = Number(current.dataset.mobileOffset ?? 0);
          setMobileEditorSelection(current, start + tag.length - offset, end + tag.length - offset);
        }
      }, 0);
      return;
    }
    insertIntoTarget(segment, tag);
  }

  function normalizeTypedTarget(value: string, filename: string) {
    const styled = apostropheMode === "curly"
      ? value.replace(/(^|[^\\])'/gu, "$1’")
      : apostropheMode === "straight" ? value.replace(/[‘’]/gu, "'") : value;
    return normalizeVivoOsApostrophes(styled, bundle?.project.profile ?? "", filename);
  }

  function cycleApostropheMode() {
    setApostropheMode((current) => current === "auto" ? "curly" : current === "curly" ? "straight" : "auto");
  }

  async function requestFullScreenInstall() {
    const prompt = installPrompt.current;
    if (prompt) {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === "accepted") setMessage("Installing the full-screen phone app…");
      else setMessage("Installation cancelled. The browser version remains fully usable.");
    } else {
      setMessage("For the largest iPhone workspace: tap Safari Share, then Add to Home Screen. The installed app removes Safari's bars and keeps the job available offline.");
    }
    setSheet(null);
  }

  function moveQuickCategoryFromKeyboard(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const tablist = event.currentTarget.parentElement;
    let next = index;
    if (["ArrowRight", "ArrowDown"].includes(event.key)) next = (index + 1) % QUICK_CATEGORIES.length;
    else if (["ArrowLeft", "ArrowUp"].includes(event.key)) next = (index - 1 + QUICK_CATEGORIES.length) % QUICK_CATEGORIES.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = QUICK_CATEGORIES.length - 1;
    else return;
    event.preventDefault();
    setQuickCategory(QUICK_CATEGORIES[next]);
    window.requestAnimationFrame(() => tablist?.querySelectorAll<HTMLButtonElement>("[role='tab']")[next]?.focus());
  }

  function openUploadedReference(reference: MobileReferenceFile) {
    const bytes = portableReferenceBytes(reference);
    const url = URL.createObjectURL(new Blob([bytes], { type: reference.mime_type }));
    const opened = window.open(url, "_blank", "noopener");
    if (!opened) downloadBlob(new Blob([bytes], { type: reference.mime_type }), reference.name);
    window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
  }

  async function previewUploadedReference(reference: MobileReferenceFile, focus?: { sheet?: string | null; row?: number | null }) {
    const query = selected?.source_text.replace(/⟦(?:\/?T|R)\d+⟧/g, "").trim() ?? "";
    const layout = portableLayoutPreview(bundle?.reference_files ?? [], reference);
    if (reference.mime_type === "application/pdf" || reference.name.toLocaleLowerCase().endsWith(".pdf")) {
      const url = URL.createObjectURL(new Blob([portableReferenceBytes(reference)], { type: "application/pdf" }));
      setReferencePreview({ reference, kind: "pdf", query, url });
      return;
    }
    setMessage(`Indexing ${reference.name}…`);
    const docx = await extractPortableDocxPreview(reference);
    if (docx) {
      const layoutUrl = layout ? URL.createObjectURL(new Blob([portableReferenceBytes(layout)], { type: "application/pdf" })) : undefined;
      setReferencePreview({ reference, kind: "docx", viewMode: "native", query, docx, layoutUrl });
      setMessage(docx.warnings.length
        ? `Word reference opened as formatted browser pages · ${docx.warnings.length} semantic detail${docx.warnings.length === 1 ? "" : "s"} may be simplified${layoutUrl ? "; a staged PDF is also available for comparison" : ""}.`
        : `Word reference opened as formatted browser pages${layoutUrl ? " · staged PDF comparison is available" : ""}.`);
      return;
    }
    const formatted = await extractPortableOdtPreview(reference);
    if (formatted) {
      const layoutUrl = layout ? URL.createObjectURL(new Blob([portableReferenceBytes(layout)], { type: "application/pdf" })) : undefined;
      setReferencePreview({ reference, kind: "formatted", viewMode: "native", query, docx: formatted, layoutUrl });
      setMessage(`OpenDocument reference decoded on this phone with native structure and formatting${layoutUrl ? " · staged PDF comparison is available" : ""}.`);
      return;
    }
    const workbook = await extractPortableWorkbookGrid(reference);
    if (workbook) {
      setReferencePreview({ reference, kind: "workbook", query, workbook, focusSheet: focus?.sheet, focusRow: focus?.row });
      return;
    }
    if (layout) {
      const url = URL.createObjectURL(new Blob([portableReferenceBytes(layout)], { type: "application/pdf" }));
      setReferencePreview({ reference, kind: "pdf", viewMode: "layout", query, url });
      setMessage("This document opened in its page-faithful staged preview.");
      return;
    }
    const text = await extractPortableReferenceText(reference);
    if (text) {
      setReferencePreview({ reference, kind: "text", query, text });
      return;
    }
    setMessage("This legacy or binary reference is opening in the iPhone viewer.");
    openUploadedReference(reference);
  }

  function insertNextTag() {
    const editor = selected ? activeMobileTargetEditor(selected.id) : null;
    if (!selected || !editor) return;
    const sourceTags = selected.source_text.match(/⟦(?:\/?T|R)\d+⟧/g) ?? [];
    const targetTags = selected.target.match(/⟦(?:\/?T|R)\d+⟧/g) ?? [];
    const used = new Map<string, number>();
    for (const tag of targetTags) used.set(tag, (used.get(tag) ?? 0) + 1);
    const seen = new Map<string, number>();
    const next = sourceTags.find((tag) => {
      const count = (seen.get(tag) ?? 0) + 1;
      seen.set(tag, count);
      return count > (used.get(tag) ?? 0);
    });
    if (!next) { setMessage("Every source tag is already present in the target."); return; }
    insertIntoTarget(selected, next);
  }

  async function handleImport(event: ChangeEvent<HTMLInputElement>) {
    const workbookIntent = event.currentTarget === resourceInputRef.current ? "terminology" as const : "auto" as const;
    const chosen = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!chosen.length || busy) return;
    setIntakeAttempted(true);
    const controller = new AbortController();
    importController.current = controller;
    setBusy(true);
    setMessage(`Opening ${chosen.length} selected file${chosen.length === 1 ? "" : "s"}…`);
    try {
      // Yield once so the progress toast and disabled import control paint
      // before ZIP decompression occupies mobile Safari's main thread.
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      const previousReferences = bundle?.reference_files?.length ?? 0;
      const previousTmFiles = bundle?.resources?.translation_memories.length ?? 0;
      const previousGlossaryFiles = bundle?.resources?.glossaries.length ?? 0;
      const previousTmEntries = bundle?.tm_entries?.length ?? 0;
      const previousGlossaryEntries = bundle?.glossary_entries?.length ?? 0;
      const previousReferenceIds = new Set((bundle?.reference_files ?? []).map((item) => item.id));
      let imported = await importMobileJobFiles(chosen, bundle, (progress) => setMessage(progress), { workbookIntent, signal: controller.signal });
      const looseImageSelection = chosen.length <= 8 && chosen.every((file) => file.type.startsWith("image/") || /\.(?:png|jpe?g|webp|gif|avif|heic|heif)$/i.test(file.name));
      if (looseImageSelection && selected && imported.companions[selected.id]) {
        const addedImages = (imported.reference_files ?? []).filter((item) => item.mime_type.startsWith("image/") && !previousReferenceIds.has(item.id));
        if (addedImages.length) {
          const companion = imported.companions[selected.id];
          const existing = new Set(companion.screenshots.map((item) => item.filename.toLocaleLowerCase()));
          imported = {
            ...imported,
            companions: {
              ...imported.companions,
              [selected.id]: {
                ...companion,
                screenshots: [...companion.screenshots, ...addedImages.filter((item) => !existing.has(item.name.toLocaleLowerCase())).map((item) => ({
                  id: `phone-manual-${selected.id}-${item.id}`,
                  role: "context",
                  filename: item.name,
                  image_url: "",
                  reference_file_id: item.id,
                  ocr_text: "",
                  location: "Manually attached on phone",
                }))],
              },
            },
          };
        }
      }
      if (controller.signal.aborted) throw new DOMException("Import cancelled.", "AbortError");
      const requiredBytes = estimateMobileBundleStorageBytes(imported);
      const beforeSaveStatus = await getMobileStorageStatus(requiredBytes);
      if (controller.signal.aborted) throw new DOMException("Import cancelled.", "AbortError");
      setStorageStatus(beforeSaveStatus);
      if (beforeSaveStatus.pressure === "insufficient") setMessage("Phone storage looks tight; attempting an atomic save without replacing the open job…");
      let storedDurably = true;
      let storageFailure: unknown = null;
      let afterSaveStatus = beforeSaveStatus;
      try { await saveImportedMobileBundle(imported, controller.signal); }
      catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") throw error;
        storedDurably = false;
        storageFailure = error;
      }
      if (storedDurably) {
        // The atomic IndexedDB transaction has committed. From this point the
        // import is finalizing rather than cancelable, so remove the cancel
        // affordance before requesting persistence or activating the job.
        if (importController.current === controller) importController.current = null;
        setMessage("Finalizing the saved phone job…");
        safeLocalSet("tcc-mobile-active-job", "imported");
        setLocalAuthority(true);
        await requestMobileStoragePersistence();
        afterSaveStatus = await getMobileStorageStatus(requiredBytes);
        setStorageStatus(afterSaveStatus);
      } else if (bundle) {
        throw new Error(isMobileQuotaError(storageFailure)
          ? "This import does not fit in durable phone storage. The open job was left unchanged; free device space or export it before trying again."
          : "This import could not be saved reliably. The open job was left unchanged; close private browsing or old TCC tabs and try again.");
      }
      const addedReferences = Math.max(0, (imported.reference_files?.length ?? 0) - previousReferences);
      const addedTmFiles = Math.max(0, (imported.resources?.translation_memories.length ?? 0) - previousTmFiles);
      const addedGlossaryFiles = Math.max(0, (imported.resources?.glossaries.length ?? 0) - previousGlossaryFiles);
      const addedTmEntries = Math.max(0, (imported.tm_entries?.length ?? 0) - previousTmEntries);
      const addedGlossaryEntries = Math.max(0, (imported.glossary_entries?.length ?? 0) - previousGlossaryEntries);
      const exactRows = Object.values(imported.companions).filter((item) => item.reference_row).length;
      const linkedScreenshots = Object.values(imported.companions).reduce((total, item) => total + item.screenshots.length, 0);
      const attached: string[] = [];
      if (addedTmFiles) attached.push(`${addedTmFiles} TM${addedTmFiles === 1 ? "" : "s"} added${addedTmEntries ? ` · ${addedTmEntries} searchable entr${addedTmEntries === 1 ? "y" : "ies"}` : ""}`);
      const newGlossaries = (imported.resources?.glossaries ?? []).slice(previousGlossaryFiles);
      const autoDetectedGlossaries = newGlossaries.filter((item) => item.note?.startsWith("Automatically detected")).length;
      if (addedGlossaryFiles) attached.push(`${addedGlossaryFiles} glossary file${addedGlossaryFiles === 1 ? "" : "s"} ${autoDetectedGlossaries ? "auto-detected" : "added"}${addedGlossaryEntries ? ` · ${addedGlossaryEntries} term${addedGlossaryEntries === 1 ? "" : "s"}` : ""}`);
      else if (addedGlossaryEntries) attached.push(`${addedGlossaryEntries} glossary term${addedGlossaryEntries === 1 ? "" : "s"} indexed`);
      // A glossary workbook is also retained as a viewable reference. Avoid
      // reporting that same selected file twice in the compact status strip.
      const referenceOnlyFiles = Math.max(0, addedReferences - addedGlossaryFiles);
      if (referenceOnlyFiles) attached.push(`${referenceOnlyFiles} reference file${referenceOnlyFiles === 1 ? "" : "s"} added`);
      if ((addedReferences || referenceOnlyFiles) && exactRows) attached.push(`${exactRows} exact row${exactRows === 1 ? "" : "s"}`);
      if ((addedReferences || referenceOnlyFiles) && linkedScreenshots) attached.push(`${linkedScreenshots} linked screenshot${linkedScreenshots === 1 ? "" : "s"}`);
      const sameOpenJob = imported.project.id === bundle?.project.id;
      const readyMessage = sameOpenJob && attached.length
        ? `${attached.join(" · ")}.`
        : sameOpenJob && (chosen.length > 0)
          ? "The selected resources were already attached; no duplicate was added."
          : `${imported.files.length} bilingual file(s) opened automatically · ${imported.segments.length} segments · ${exactRows} exact row${exactRows === 1 ? "" : "s"} · ${linkedScreenshots} linked screenshot${linkedScreenshots === 1 ? "" : "s"}.`;
      const persistenceNote = storedDurably && afterSaveStatus.persisted === false
        ? " Saved in best-effort browser storage; add TCC to the Home Screen and keep a recovery ZIP for maximum safety."
        : storedDurably ? "" : " Browser storage is unavailable, so export a draft before closing this tab.";
      await activatePlainBundle(imported, `${readyMessage}${imported.import_warnings?.length ? ` ${imported.import_warnings.join(" ")}` : ""}${persistenceNote}`, imported.project.id === bundle?.project.id ? selected?.id : null);
      setSheet(null);
    } catch (error) {
      setMessage(error instanceof DOMException && error.name === "AbortError"
        ? "Import cancelled. The previously open job was left unchanged."
        : error instanceof Error ? error.message : "The selected files could not be opened safely.");
    } finally {
      if (importController.current === controller) importController.current = null;
      setBusy(false);
    }
  }

  function cancelImport() {
    const active = importController.current;
    if (!active || active.signal.aborted) return;
    active.abort();
    setMessage("Cancelling import…");
  }

  async function restoreBundledJob() {
    if (!servedJobAvailable) {
      setMessage("No saved served-job branch is available. The imported phone job remains open.");
      return;
    }
    const synchronous = safeLocalSet("tcc-mobile-active-job", "served");
    let durable = false;
    try {
      await saveMobileActiveJobPreference("served");
      durable = true;
    } catch { /* The synchronous marker below can still select the served branch. */ }
    if (!synchronous && !durable) {
      setMessage("Safari could not save the branch switch. The imported phone job remains open so no work is lost.");
      return;
    }
    setLocalAuthority(false);
    window.location.reload();
  }

  async function protectPhoneStorage() {
    const granted = await requestMobileStoragePersistence();
    const status = await getMobileStorageStatus(bundle ? estimateMobileBundleStorageBytes(bundle) : 0);
    setStorageStatus(status);
    setMessage(granted || status.persisted
      ? "TCC storage is protected from automatic browser eviction on this device."
      : standalone
        ? "The job is saved in the Home Screen app, but keep a recovery ZIP for device-storage emergencies."
        : "Safari kept best-effort storage. Add TCC to the Home Screen, reopen it there, and keep a recovery ZIP.");
  }

  function startRowGesture(event: React.PointerEvent<HTMLElement>, segment: MobileSegment) {
    if ((event.target as HTMLElement).closest("textarea,button,input,a,[contenteditable='true']")) return;
    const targetEditor = (event.target as HTMLElement).closest<HTMLElement>("[data-mobile-target]");
    const gesture = {
      segmentId: segment.id,
      startX: event.clientX,
      startY: event.clientY,
      latestX: event.clientX,
      latestY: event.clientY,
      startedAt: Date.now(),
      canSwipe: segment.id === selectedId && editingSegmentId !== segment.id,
      pane: null,
      holdTimer: null as number | null,
      held: false,
      targetEditor,
    };
    rowGesture.current = gesture;
    if (targetEditor && !segment.locked && editingSegmentId !== segment.id) {
      gesture.holdTimer = window.setTimeout(() => {
        const active = rowGesture.current;
        if (active !== gesture || Math.hypot(active.latestX - active.startX, active.latestY - active.startY) > 8) return;
        active.held = true;
        active.holdTimer = null;
        const position = mobileEditorPointPosition(targetEditor, active.startX, active.startY);
        beginEditing(segment, position);
        navigator.vibrate?.(8);
      }, MOBILE_EDIT_HOLD_MS);
    }
  }

  function moveRowGesture(event: React.PointerEvent<HTMLElement>) {
    const gesture = rowGesture.current;
    if (!gesture) return;
    gesture.latestX = event.clientX;
    gesture.latestY = event.clientY;
    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    if (Math.hypot(deltaX, deltaY) > 8 && gesture.holdTimer != null) {
      window.clearTimeout(gesture.holdTimer);
      gesture.holdTimer = null;
    }
    const selection = window.getSelection();
    if ((selection && !selection.isCollapsed) || (Date.now() - gesture.startedAt > 420 && Math.abs(deltaX) < 52)) {
      if (gesture.holdTimer != null) window.clearTimeout(gesture.holdTimer);
      rowGesture.current = null;
      setGesturePreview(null);
      return;
    }
    if (!gesture.canSwipe) return;
    if (Math.abs(deltaX) < 38 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
    gesture.pane = deltaX < 0 ? "visuals" : "confirm";
    setGesturePreview(gesture.pane);
    if (gesture.pane === "visuals" && event.clientX <= 18) {
      setCompanionTab("visuals");
      setSheet("companion");
      setGesturePreview(null);
      rowGesture.current = null;
    }
  }

  function endRowGesture() {
    const gesture = rowGesture.current;
    const pane = gesture?.pane;
    const distance = gesture ? gesture.latestX - gesture.startX : 0;
    const segment = gesture ? segmentsRef.current.find((item) => item.id === gesture.segmentId) : null;
    if (gesture?.holdTimer != null) window.clearTimeout(gesture.holdTimer);
    rowGesture.current = null;
    setGesturePreview(null);
    if (gesture?.held) return;
    if (pane === "confirm" && distance >= 72 && segment && !segment.locked) confirm(segment);
    else if (gesture && Math.abs(gesture.latestX - gesture.startX) < 9 && Math.abs(gesture.latestY - gesture.startY) < 9 && Date.now() - gesture.startedAt < 500 && segment) {
      finishEditing();
      setSelectedId(segment.id);
    }
  }

  async function renderReport(issues: MobileQaIssue[]) {
    if (!bundle) throw new Error("Job is not loaded.");
    return renderMobileXbenchReport(issues, bundle.qa_profile);
  }

  async function createPackage(share: boolean) {
    if (!bundle || busy) return;
    setBusy(true);
    try {
      await submitPatch(false);
      const draft = !completion.complete;
      let report: Uint8Array | null = null;
      try { report = await renderReport(qa.issues.filter((item) => item.code !== "unsupported_checklist_expression")); }
      catch (error) {
        if (!draft) throw error;
        setMessage("Draft package created without the QA workbook because the renderer is offline.");
      }
      setMessage("Compressing the phone-authority bilingual and QA report…");
      const blob = await buildMobileZip(bundle, segments, report, draft);
      const safe = mobileDeliveryBaseName(bundle);
      const filename = `${safe}${draft ? "_DRAFT_NOT_FOR_DELIVERY" : ""}.zip`;
      const file = new File([blob], filename, { type: "application/zip" });
      if (share && !draft && navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        try {
          await navigator.share({ title: bundle.project.name, text: `Completed ${bundle.project.client} translation package`, files: [file] });
          setMessage("Package handed to the iOS share sheet. Review the recipient before sending.");
        } catch (error) {
          if (shareWasCancelled(error)) {
            setMessage("Sharing cancelled. The final remains available from Final package.");
          } else {
            downloadBlob(blob, filename);
            setMessage("The share sheet was unavailable, so the verified final ZIP was downloaded to this phone instead.");
          }
        }
      } else {
        downloadBlob(blob, filename);
        setMessage(draft ? "Draft downloaded and marked NOT FOR DELIVERY." : "Verified delivery package downloaded.");
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Package creation failed.");
    } finally { setBusy(false); }
  }

  async function createSupportPackage(kind: "codex" | "ai") {
    if (!bundle || busy) return;
    setBusy(true);
    try {
      await submitPatch(false);
      let report: Uint8Array | null = null;
      if (kind === "ai") {
        try { report = await renderReport(qa.issues.filter((item) => item.code !== "unsupported_checklist_expression")); }
        catch { /* The readable CSV/JSON review pack remains useful without BIFF rendering. */ }
      }
      setMessage(kind === "codex" ? "Compressing the recovery pack…" : "Compressing the AI review pack and references…");
      const blob = kind === "codex"
        ? await buildMobileCodexRecoveryPack(bundle, segments, qa.issues)
        : await buildMobileAiReviewPack(bundle, segments, qa.issues, report);
      const safe = mobileDeliveryBaseName(bundle);
      const filename = `${safe}_${kind === "codex" ? "Codex_Recovery" : "AI_Review"}.zip`;
      const file = new File([blob], filename, { type: "application/zip" });
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        try {
          await navigator.share({
            title: kind === "codex" ? `${bundle.project.name} phone review` : `${bundle.project.name} AI review pack`,
            text: kind === "codex" ? "Phone-authority TCC review patch for Codex" : "Bilingual and context package for independent AI review",
            files: [file],
          });
          setMessage(kind === "codex" ? "Codex recovery pack opened in the iOS share sheet." : "AI review pack opened in the iOS share sheet.");
        } catch (error) {
          if (shareWasCancelled(error)) {
            setMessage("Sharing cancelled. Tap the pack again whenever you are ready.");
          } else {
            downloadBlob(blob, filename);
            setMessage(kind === "codex" ? "Share was unavailable; the Codex recovery pack was downloaded instead." : "Share was unavailable; the AI review pack was downloaded instead.");
          }
        }
      } else {
        downloadBlob(blob, filename);
        setMessage(kind === "codex" ? "Codex recovery pack downloaded." : "AI review pack downloaded.");
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Support package creation failed.");
    } finally { setBusy(false); }
  }

  const uploadInput = <input ref={fileInputRef} className="mobile-file-input" type="file" multiple tabIndex={-1} aria-hidden="true" accept=".zip,.json,.sdlxliff,.xlf,.xliff,.tmx,.sdltm,.tbx,.sdltb,.xlsx,.xlsm,.xls,.ods,.csv,.tsv,.pdf,.doc,.docx,.odt,.rtf,.ppt,.pptx,.odp,.txt,.md,.html,.htm,.png,.jpg,.jpeg,.webp,.gif,.avif,.heic,.heif,application/zip,application/json,image/*" onChange={(event) => void handleImport(event)} />;
  const resourceInput = <input ref={resourceInputRef} className="mobile-file-input" type="file" multiple tabIndex={-1} aria-hidden="true" accept=".zip,.tmx,.sdltm,.tbx,.sdltb,.xlsx,.xlsm,.xls,.ods,.csv,.tsv,application/zip" onChange={(event) => void handleImport(event)} />;
  const referenceInput = <input ref={referenceInputRef} className="mobile-file-input" type="file" multiple tabIndex={-1} aria-hidden="true" accept=".zip,.xlsx,.xlsm,.xls,.ods,.csv,.tsv,.pdf,.doc,.docx,.odt,.rtf,.ppt,.pptx,.odp,.txt,.md,.html,.htm,.png,.jpg,.jpeg,.webp,.gif,.avif,.heic,.heif,application/zip,image/*" onChange={(event) => void handleImport(event)} />;
  if (loading) return <main className="mobile-loading" aria-busy="true"><span className="mobile-spinner" /><p role="status" aria-live="polite">{message}</p></main>;
  if (!bundle || !selected) {
    const bareIntake = !new URLSearchParams(window.location.search).has("job") && !new URLSearchParams(window.location.search).has("mobileJob");
    const intakeDetail = busy || intakeAttempted
      ? message
      : "Client ZIP, SDLXLIFF/XLIFF, Phrase bilingual DOCX or prepared TCC package. References and resources are detected automatically.";
    return <main className="mobile-loading error">
      {bareIntake ? <FileUp /> : <X />}
      <h1>{bareIntake ? "Open a translation job" : "Job unavailable"}</h1>
      {uploadInput}
      <button className="mobile-primary-action" disabled={busy} onClick={() => fileInputRef.current?.click()}>{busy ? <span className="mobile-intake-spinner" /> : <FileUp />}{busy ? "Preparing…" : "Choose files"}</button>
      <p role={busy ? "status" : intakeAttempted || !bareIntake ? "alert" : undefined} aria-live={busy ? "polite" : intakeAttempted || !bareIntake ? "assertive" : undefined}>{bareIntake ? intakeDetail : message}</p>
      {busy && importController.current && <button className="mobile-intake-cancel" onClick={cancelImport}><X />Cancel import</button>}
    </main>;
  }

  const selectedTagIssues = qa.issues.filter((item) => item.segment_id === selected.id && item.code === "inline_tag_mismatch").length;
  const selectedTagInventory = buildTagInventory(selected.source_text, selected.target);
  const editAccessoryVisible = editingSegmentId === selected.id && Boolean(selectedTagInventory.length || companions?.terms.length);
  const flaggedCount = segments.filter((item) => item.review_status === "flagged").length;
  const exactReferenceCount = (companions?.screenshots.length ?? 0) + companionDocuments.length + (referenceRow ? 1 : 0);
  return <main className={`mobile-shell ${(sheet === "menu" || tmOpen) ? "top-pane-open" : ""} ${editAccessoryVisible ? "has-edit-accessory" : ""}`} style={{ "--mobile-editor-font": `${fontSize}px` } as CSSProperties} aria-busy={busy || undefined}>
    {uploadInput}
    {resourceInput}
    {referenceInput}
    <header className="mobile-header">
      <button onClick={() => { setTmOpen(false); setSheet((current) => current === "jobs" ? null : "jobs"); }} aria-label="Browse served jobs"><FolderOpen /></button>
      <div>
        <button className="mobile-header-title" onClick={() => { setTmOpen(false); setSheet((current) => current === "menu" ? null : "menu"); }} aria-label="Toggle compact job menu">
          <span><small aria-label={`${bundle.project.source_locale} to ${bundle.project.target_locale}; ${confirmedCount} of ${segments.length} segments confirmed`} title={`${bundle.project.source_locale} → ${bundle.project.target_locale}`}>{localeFlag(bundle.project.source_locale)} → {localeFlag(bundle.project.target_locale)} · {confirmedCount}/{segments.length}</small><strong>{bundle.project.name}</strong></span>
          <ChevronDown className={sheet === "menu" ? "rotated" : ""} />
        </button>
      </div>
      <div className="mobile-header-actions"><button onClick={() => setSheet("browse")} aria-label="Search segments"><Search /></button><button onClick={() => fileInputRef.current?.click()} disabled={busy} aria-label="Open a job or add reference files"><FileUp /></button></div>
    </header>

    {(sheet === "menu" || tmOpen) && <section className="mobile-top-pane">
      {sheet === "menu" ? <div className="mobile-quick-menu">
        <nav role="tablist" aria-label="Job tools">{QUICK_CATEGORIES.map((category, index) => <button key={category} id={`mobile-tools-tab-${category}`} role="tab" aria-controls="mobile-tools-panel" aria-selected={quickCategory === category} tabIndex={quickCategory === category ? 0 : -1} className={quickCategory === category ? "active" : ""} onClick={() => setQuickCategory(category)} onKeyDown={(event) => moveQuickCategoryFromKeyboard(event, index)}>{category[0].toLocaleUpperCase() + category.slice(1)}</button>)}</nav>
        <div id="mobile-tools-panel" className={`mobile-quick-panel panel-${quickCategory}`} role="tabpanel" aria-labelledby={`mobile-tools-tab-${quickCategory}`}>
          {quickCategory === "files" && <><button onClick={() => fileInputRef.current?.click()} disabled={busy}><FileUp />Open job</button>{servedJobAvailable && localAuthority && <button onClick={() => void restoreBundledJob()}><FolderOpen />Served job</button>}<button onClick={() => void submitPatch()}><Save />Save draft</button>{!standalone && <button onClick={() => void requestFullScreenInstall()} aria-label="Use Translation Command Center full screen"><Smartphone />Full screen</button>}<button className={`mobile-storage-button pressure-${storageStatus?.pressure ?? "unknown"}`} onClick={() => void protectPhoneStorage()} title={mobileStorageDescription(storageStatus)}><Save /><span><b>{mobileStorageLabel(storageStatus)}</b><small>{mobileStorageCapacity(storageStatus)}</small></span></button></>}
          {quickCategory === "resources" && <><button onClick={() => resourceInputRef.current?.click()} disabled={busy}><FileUp />Add TM/TB</button><button onClick={() => referenceInputRef.current?.click()} disabled={busy}><FileUp />Add refs</button><button onClick={() => { setCompanionTab("visuals"); setSheet("companion"); }}><FolderOpen />Open refs</button><span className="mobile-resource-total" aria-label={`${bundle.resources?.translation_memories.length ?? 0} translation-memory files, ${bundle.resources?.glossaries.length ?? 0} termbase files, ${bundle.tm_entries?.length ?? 0} searchable translation-memory entries`}><b>{bundle.resources?.translation_memories.length ?? 0}/{bundle.resources?.glossaries.length ?? 0}</b><small>TM/TB</small></span></>}
          {quickCategory === "editor" && <><button onClick={() => setFontSize((value) => Math.max(11, value - 1))}>A−</button><span>{fontSize}px</span><button onClick={() => setFontSize((value) => Math.min(32, value + 1))}>A+</button><button onClick={() => setSheet("browse")}><Search />Find</button><button onClick={() => updateSegment(selected.id, { target: selected.source_text, review_status: "pending" })}><Copy />Copy</button><button onClick={insertNextTag}><Tags />Tag</button><button className={selected.review_status === "flagged" ? "active" : ""} onClick={() => updateSegment(selected.id, { review_status: selected.review_status === "flagged" ? "pending" : "flagged" })}><Flag />Flag</button><button onClick={() => updateSegment(selected.id, { target: "", review_status: "pending" })}><Trash2 />Clear</button><button className="mobile-editor-wide" onClick={() => setSpecialCharactersOpen((value) => !value)} aria-expanded={specialCharactersOpen}><Pilcrow />Characters</button><button className="mobile-editor-wide" onClick={cycleApostropheMode} title="Cycle automatic, curly and straight apostrophe input"><Quote />{apostropheMode === "auto" ? "Apostrophe auto" : apostropheMode === "curly" ? "Curly ’" : "Straight '"}</button>{specialCharactersOpen && <div className="mobile-character-picker" aria-label="Insert special character">{MOBILE_SPECIAL_CHARACTERS.map((item) => <button key={item.label} onClick={() => insertIntoTarget(selected, item.value)} title={item.title}><b>{item.glyph}</b><small>{item.label}</small></button>)}</div>}</>}
          {quickCategory === "delivery" && <><button onClick={() => void createPackage(false)} disabled={busy}><Download />{completion.complete ? "Final ZIP" : "Draft ZIP"}</button><button onClick={() => void createPackage(true)} disabled={busy || !completion.complete}><Share2 />Send final</button><button onClick={() => void createSupportPackage("codex")} disabled={busy}><Save />Codex pack</button><button onClick={() => void createSupportPackage("ai")} disabled={busy}><Share2 />AI review</button></>}
        </div>
      </div> : <div className={`mobile-tm-strip ${tmResults.length ? "has-results" : "empty"}`}>
        <header><Languages /><label><span className="mobile-visually-hidden">Search translation memory</span><input value={tmQuery} onChange={(event) => setTmQuery(event.target.value)} placeholder="TM concordance…" inputMode="search" /></label><output aria-live="polite" aria-label={`${tmResults.length} translation-memory result${tmResults.length === 1 ? "" : "s"}`}>{tmResults.length}</output><button onClick={() => setTmOpen(false)} aria-label="Close translation memory"><X /></button></header>
        {!tmResults.length
          ? <p className="mobile-visually-hidden" role="status">No translation-memory result. Change the query or close this compact search bar.</p>
            : <div>{tmResults.map((match) => <button key={`${match.id}-${match.target}`} onClick={() => {
              updateSegment(selected.id, { target: match.target, review_status: "pending" });
              setTmOpen(false);
              beginEditing({ ...selected, target: match.target, review_status: "pending" }, match.target.length);
            }}><b>{match.score}%</b><span><strong>{match.source}</strong><em>{match.target}</em><small>{match.origin}</small></span></button>)}</div>}
      </div>}
    </section>}

    <section ref={segmentScrollRef} className="mobile-segment-scroll" aria-label="Scrollable bilingual segments">
      <div className="mobile-virtual-list" role="list" aria-label={`${segments.length} bilingual segments`} style={{ height: `${segmentVirtualizer.getTotalSize()}px` }}>
        {segmentVirtualizer.getVirtualItems().map((virtualRow) => {
          const segment = segments[virtualRow.index];
          const rowSelected = segment.id === selected.id;
          const rowCompanions = bundle.companions[segment.id];
          const fileStart = virtualRow.index === 0 || segments[virtualRow.index - 1]?.filename !== segment.filename;
          const previousFile = virtualRow.index ? segments[virtualRow.index - 1]?.filename : null;
          const longSegment = isMobileLongSegment(segment);
          return <article
            role="listitem"
            key={segment.id}
            ref={segmentVirtualizer.measureElement}
            data-index={virtualRow.index}
            data-mobile-segment={segment.id}
            aria-posinset={virtualRow.index + 1}
            aria-setsize={segments.length}
            className={`mobile-segment-row ${rowSelected ? "selected" : ""} ${editingSegmentId === segment.id ? "editing" : "reviewing"} ${isConfirmed(segment.review_status) ? "confirmed" : "draft"} status-${segment.review_status} ${fileStart ? "file-start" : ""} ${longSegment ? "mobile-long-segment" : ""} ${longSegment && rowSelected ? "mobile-long-selected" : ""}`}
            style={{ transform: `translateY(${virtualRow.start}px)` }}
            onPointerDownCapture={(event) => startRowGesture(event, segment)}
            onPointerMove={(event) => moveRowGesture(event)}
            onPointerUp={endRowGesture}
            onPointerCancel={endRowGesture}
          >
            {fileStart && (previousFile
              ? <div className="mobile-file-transition" aria-label={`From ${previousFile} to ${segment.filename}`}>
                  <div className="mobile-file-boundary previous" title={previousFile}><span>From</span><strong>{shortFileLabel(previousFile)}</strong></div>
                  <div className="mobile-file-boundary next" title={segment.filename}><span>To</span><strong>{shortFileLabel(segment.filename)}</strong></div>
                </div>
              : <div className="mobile-file-transition first"><div className="mobile-file-boundary next" title={segment.filename}><span>File</span><strong>{shortFileLabel(segment.filename)}</strong></div></div>)}
            <button
              className="mobile-row-status"
              aria-label={rowSelected && !segment.locked ? `Confirm segment ${segment.ordinal} and move to the next draft` : `Select segment ${segment.ordinal}: ${segment.review_status}`}
              title={rowSelected && !segment.locked ? "Confirm and move to next draft" : `Segment ${segment.ordinal}: ${segment.review_status}`}
              onClick={() => {
                if (rowSelected && !segment.locked) confirm(segment);
                else { finishEditing(); setSelectedId(segment.id); }
              }}
            ><span>{statusGlyph(segment)}</span><small>{segment.ordinal}</small></button>
            {longSegment && rowSelected
              ? <MobileLongSegmentEditor
                  key={segment.id}
                  segment={segment}
                  language={bundle.project.target_locale.replace("_", "-")}
                  terms={rowCompanions?.terms ?? []}
                  editable={editingSegmentId === segment.id}
                  normalize={(value) => normalizeTypedTarget(value, segment.filename)}
                  onTagClick={(tag) => insertSourceTag(segment, tag)}
                  onRequestEdit={(position) => beginEditing(segment, position)}
                  onUndo={() => restoreTargetHistory(segment, "undo")}
                  onRedo={() => restoreTargetHistory(segment, "redo")}
                  onChange={(target) => updateSegment(segment.id, { target, review_status: "pending" })}
                />
              : <><div id={`mobile-source-${segment.id}`} className="mobile-row-source" aria-label={`Source segment ${segment.ordinal}`}><p><HighlightedSource text={segment.source_text} target={segment.target} terms={rowCompanions?.terms ?? []} onTagClick={(tag) => insertSourceTag(segment, tag)} /></p></div>
                <div className="mobile-row-target"><MobileTargetEditor
                  segment={segment}
                  editable={editingSegmentId === segment.id}
                  describedBy={`mobile-source-${segment.id}`}
                  language={bundle.project.target_locale.replace("_", "-")}
                  normalize={(value) => normalizeTypedTarget(value, segment.filename)}
                  onRequestEdit={(position) => beginEditing(segment, position)}
                  onFocus={(editor) => { setSelectedId(segment.id); resizeTargetField(editor); }}
                  onUndo={() => restoreTargetHistory(segment, "undo")}
                  onRedo={() => restoreTargetHistory(segment, "redo")}
                  onChange={(target, editor) => {
                    updateSegment(segment.id, { target, review_status: "pending" });
                    window.requestAnimationFrame(() => {
                      resizeTargetField(editor);
                      scrollMobileCaretIntoView(editor);
                    });
                  }}
                /></div></>}
          </article>;
        })}
      </div>
    </section>

    <div className={`mobile-announcement ${noticeVisible ? "visible" : ""} ${noticeExpanded ? "expanded" : ""}`} role={IMPORTANT_MOBILE_MESSAGE.test(message) ? "alert" : "status"} aria-live={IMPORTANT_MOBILE_MESSAGE.test(message) ? "assertive" : "polite"}><span title={message}>{message}</span>{(message.length > 84 || importController.current) && <nav aria-label="Status message controls">{message.length > 84 && <button onClick={() => setNoticeExpanded((value) => !value)} aria-label={noticeExpanded ? "Collapse status message" : "Show full status message"} aria-expanded={noticeExpanded}><ChevronDown className={noticeExpanded ? "rotated" : ""} /></button>}{importController.current ? <button onPointerDown={cancelImport} onClick={cancelImport} aria-label="Cancel file import" title="Cancel file import"><X /></button> : <button onClick={() => setNoticeVisible(false)} aria-label="Dismiss status message"><X /></button>}</nav>}</div>
    {editAccessoryVisible && <MobileEditAccessory
      segment={selected}
      tagInventory={selectedTagInventory}
      terms={companions?.terms ?? []}
      onInsertTag={(tag) => insertIntoTarget(selected, tag)}
      onInsertTerm={(term) => insertIntoTarget(selected, term)}
      onOpenTerms={() => { setCompanionTab("terms"); setSheet("companion"); }}
    />}
    <footer className="mobile-status-bar" aria-label="Job status and companion controls">
      {editingSegmentId
        ? <><button aria-label="Undo target edit" disabled={!targetHistory.current.get(editingSegmentId)?.past.length} onClick={() => restoreTargetHistory(selected, "undo")}><Undo2 /><span>Undo</span></button><button aria-label="Redo target edit" disabled={!targetHistory.current.get(editingSegmentId)?.future.length} onClick={() => restoreTargetHistory(selected, "redo")}><Redo2 /><span>Redo</span></button><button className="mobile-editing-done" aria-label="Finish editing" onClick={finishEditing}><Check /><span>Done</span></button></>
        : <><button aria-label="Search translation memory" className={tmOpen ? "active" : ""} onClick={() => tmOpen ? setTmOpen(false) : openTmSearch()}><Languages /><span>TM</span><b>{tmResults.length}</b></button><button aria-label={`${sourceWordProgress.draft} source words remain in draft segments`} title={`${sourceWordProgress.draft} source words remain in draft segments`} onClick={() => { const draft = segments.findIndex((item) => !item.locked && !isConfirmed(item.review_status)); if (draft >= 0) { setSelectedId(segments[draft].id); segmentVirtualizer.scrollToIndex(draft, { align: "center" }); } }}><span>✎</span><b>{sourceWordProgress.draft}w</b></button><span className="confirmed" aria-label={`${sourceWordProgress.confirmed} of ${sourceWordProgress.total} source words confirmed`} title={`${sourceWordProgress.confirmed} of ${sourceWordProgress.total} source words confirmed`}><Check /><b>{sourceWordProgress.confirmed}w</b></span></>}
      <span aria-label={`${flaggedCount} flagged segments`}><Flag /><b>{flaggedCount}</b></span>
      <span className={selectedTagIssues ? "warning" : ""} aria-label={`${selectedTagIssues} tag issues and ${selected.target.length} target characters`}><Tags /><b>{selectedTagIssues}</b><i>{selected.target.length}c</i></span>
      <button aria-label={`${exactReferenceCount} exact references for this segment`} onClick={() => { setCompanionTab(exactReferenceCount || jobReferenceFiles.length ? "visuals" : "terms"); setSheet("companion"); }}><ImageIcon /><span>Refs</span><b>{exactReferenceCount}</b></button>
    </footer>

    {gesturePreview && <aside className={`mobile-gesture-preview preview-${gesturePreview}`} aria-hidden="true">{gesturePreview === "visuals" ? <>{resolvedScreenshots[0]?.image_url ? <img src={resolvedScreenshots[0].image_url} alt="" /> : <ImageIcon />}<strong>{exactReferenceCount ? `${exactReferenceCount} exact reference${exactReferenceCount === 1 ? "" : "s"} · slide to the left edge to pin` : "No exact reference linked to this string"}</strong></> : <><Check /><strong>Swipe right to confirm and jump to the next draft</strong></>}</aside>}

    {sheet && sheet !== "menu" && <div className={`mobile-sheet-backdrop companion-${companionTab}`} onPointerDown={(event) => { if (event.target === event.currentTarget) setSheet(null); }}>
      <section className={`mobile-sheet mobile-sheet-${sheet}`} role="dialog" aria-modal="true" aria-label={sheet === "jobs" ? "Browse served jobs" : sheet === "browse" ? "Browse segments" : "Reference companion"} onPointerDown={(event) => { if (sheet === "companion" && !(event.target as HTMLElement).closest("button,input,textarea,img,[data-mobile-zoom]")) companionDismissX.current = event.clientX; }} onPointerUp={(event) => { if (sheet === "companion" && companionDismissX.current != null) { const delta = event.clientX - companionDismissX.current; if ((companionTab === "terms" && delta < -70) || (companionTab !== "terms" && delta > 70)) setSheet(null); } companionDismissX.current = null; }}>
        <header className="mobile-sheet-header"><strong>{sheet === "jobs" ? "Served jobs" : sheet === "browse" ? "Find a segment" : companionTab === "terms" ? "Terminology" : companionTab === "visuals" ? "Visual references" : "Quality assurance"}</strong><button onClick={() => setSheet(null)} aria-label="Close"><X /></button></header>

        {sheet === "jobs" && <div className="mobile-job-browser">
          {!jobCatalog.length ? <p>No served-job catalog is available yet. Codex can publish jobs into TCC/jobs/aug.</p> : jobCatalog.map((entry) => {
            const active = bundle.project.id === entry.id;
            const confirmedWords = active ? sourceWordProgress.confirmed : entry.confirmed_words;
            const totalWords = active ? sourceWordProgress.total : entry.total_words;
            const progress = totalWords ? Math.min(100, Math.round(confirmedWords / totalWords * 100)) : 0;
            return <button key={entry.id} className={active ? "active" : ""} onClick={() => openCatalogJob(entry)}>
              <span className="mobile-job-client">{entry.client}</span>
              <span className="mobile-job-card-main"><strong>{entry.identifier || entry.name}</strong><small>{entry.period} · {entry.folder}</small></span>
              <b>{progress}%</b>
              <span className="mobile-job-progress" aria-label={`${confirmedWords} of ${totalWords} source words confirmed`}><i style={{ width: `${progress}%` }} /></span>
            </button>;
          })}
        </div>}

        {sheet === "browse" && <div className="mobile-browse-layout">
          <div className="mobile-search-pair">
            <label className="mobile-search"><Search /><span className="mobile-visually-hidden">Source contains</span><input value={browseQuery} onChange={(event) => setBrowseQuery(event.target.value)} placeholder="Source contains…" inputMode="search" /></label>
            <div className="mobile-search mobile-target-search"><input aria-label="Target contains or does not contain" value={browseTargetQuery} onChange={(event) => setBrowseTargetQuery(event.target.value)} placeholder="Target term…" inputMode="search" /><button type="button" className={browseTargetNot ? "active" : ""} onClick={() => setBrowseTargetNot((value) => !value)} aria-pressed={browseTargetNot}>{browseTargetNot ? "NOT" : "HAS"}</button></div>
          </div>
          <div className="mobile-browse-results">{browseResults.map((segment) => <button key={segment.id} className={segment.id === selected.id ? "selected" : ""} onClick={() => chooseSegment(segment)}><span className={`mobile-browse-status ${isConfirmed(segment.review_status) ? "confirmed" : "draft"}`}>{statusGlyph(segment)}<small>{segment.ordinal}</small></span><span><strong>{segment.source_text || "(empty source)"}</strong><small>{segment.target || "(empty target)"}</small></span></button>)}</div>
        </div>}

        {sheet === "companion" && <div className="mobile-companion-layout">
          <nav><button className={companionTab === "terms" ? "active" : ""} onClick={() => setCompanionTab("terms")}><Languages /><span>Terms</span></button><button className={companionTab === "visuals" ? "active" : ""} onClick={() => setCompanionTab("visuals")}><ImageIcon /><span>Visual</span></button><button className={companionTab === "qa" ? "active" : ""} onClick={() => setCompanionTab("qa")}><ListChecks /><span>QA</span></button></nav>
          <div className="mobile-companion-body">
            {companionTab === "terms" && (!companions?.terms.length ? <p>No applicable approved terminology.</p> : companions.terms.map((term) => { const followed = Boolean(term.target_term && containsExpectedTerm(selected.target, term.target_term)); return <button className={followed ? "term-followed" : "term-missing"} key={term.id} disabled={followed || !term.target_term} onClick={() => insertIntoTarget(selected, term.target_term ?? "")}><strong>{term.source_term}</strong><span>{term.target_term}</span><small>{followed ? `${term.authority} · followed` : `${term.authority} · missing · tap to insert at cursor`}</small></button>; }))}
            {companionTab === "visuals" && <>
              {!exactReferenceCount && <p>No screenshot, exact workbook row or matched document is linked to this string.</p>}
              {!resolvedScreenshots.length && exactReferenceCount > 0 && <div className="mobile-screenshot-status" role="status"><ImageIcon /><span><strong>No exact screenshot for this string</strong><small>The visual viewer is available, but this workbook row has no Screenshot value. Its exact row and other references remain below.</small></span></div>}
              {resolvedScreenshots.map((item) => <figure className="mobile-primary-screenshot" key={item.id}>
                {item.image_url ? <MobileZoomImage src={item.image_url} alt={item.filename} onOpen={() => window.open(item.image_url, "_blank", "noopener")} /> : <p>The linked screenshot file is unavailable.</p>}
                <figcaption><strong>{item.role}</strong><span>{item.filename}</span><small>{item.ocr_text || "Exact screenshot linked from the workbook row."}</small></figcaption>
              </figure>)}
              {referenceRow && <details className="mobile-reference-row" open={!resolvedScreenshots.length}>
                <summary>Exact reference row · {referenceRow.sheet ?? referenceRow.filename ?? "workbook"} {referenceRow.row_number ? `#${referenceRow.row_number}` : ""}{!resolvedScreenshots.length ? " · no screenshot linked" : ""}</summary>
                <div>{usefulMobileReferenceCells(referenceRow.cells ?? []).map((cell, index) => <p key={`${cell.column}-${index}`}><strong>{mobileReferenceLabel(cell.column ?? "")}</strong><span>{cell.value}</span></p>)}</div>
                {referenceRow.filename && jobReferenceFiles.some((reference) => reference.name === referenceRow.filename) && <button className="mobile-open-exact-row" onClick={() => { const reference = jobReferenceFiles.find((item) => item.name === referenceRow.filename); if (reference) void previewUploadedReference(reference, { sheet: referenceRow.sheet, row: referenceRow.row_number }); }}><ExternalLink />Open this exact row in the workbook</button>}
              </details>}
              {companionDocuments.map((document) => <button className="mobile-document-reference" key={document.id} onClick={() => document.document_url ? window.open(document.document_url, "_blank", "noopener") : setMessage(document.preview || "This reference has no portable preview.")}><FolderOpen /><span><strong>{document.label}</strong><small>{document.location ?? document.kind}</small></span><b>{Math.round((document.confidence ?? 0) * 100)}%</b></button>)}
              {Boolean(jobReferenceFiles.length) && <details className="mobile-job-references"><summary>Job documents · {jobReferenceFiles.length} · native views</summary><div>{jobReferenceFiles.map((reference) => <button key={reference.id} onClick={() => void previewUploadedReference(reference, reference.name === referenceRow?.filename ? { sheet: referenceRow?.sheet, row: referenceRow?.row_number } : undefined)}><FolderOpen /><span><strong>{reference.name}</strong><small>{formatFileSize(reference.size)} · {mobileReferenceCapability(reference, bundle?.reference_files ?? [])}</small></span></button>)}</div></details>}
            </>}
            {companionTab === "qa" && (!qa.issues.length ? <p className="mobile-qa-clear"><Check />No current QA issue.</p> : qa.issues.map((issue, index) => <button key={`${issue.segment_id}-${issue.code}-${index}`} className={`severity-${issue.severity}`} onClick={() => { const segment = segments.find((item) => item.id === issue.segment_id); if (segment) chooseSegment(segment); }}><strong>{issue.report_title ?? issue.code.replaceAll("_", " ")}</strong><span>{issue.filename} · segment {issue.segment_number}</span><small>{issue.message}</small></button>))}
          </div>
        </div>}
      </section>
    </div>}
    {referencePreview && <section className="mobile-reference-preview" role="dialog" aria-modal="true" aria-label={`Reference preview: ${referencePreview.reference.name}`}>
      <header><strong>{referencePreview.reference.name}</strong><nav>{["docx", "formatted"].includes(referencePreview.kind) && referencePreview.layoutUrl && <button onClick={() => setReferencePreview((current) => current ? { ...current, viewMode: current.viewMode === "layout" ? "native" : "layout" } : current)} aria-label={referencePreview.viewMode === "layout" ? "Return to native document view" : "Compare with staged PDF pages"} title={referencePreview.viewMode === "layout" ? "Native document" : "Staged PDF"}>{referencePreview.viewMode === "layout" ? <Pilcrow /> : <FileText />}</button>}<button onClick={() => openUploadedReference(referencePreview.reference)} aria-label="Open original reference in the iPhone viewer"><ExternalLink /></button><button onClick={() => setReferencePreview(null)} aria-label="Close reference preview"><X /></button></nav></header>
      {["docx", "formatted"].includes(referencePreview.kind) && referencePreview.viewMode === "layout" && referencePreview.layoutUrl
        ? <MobilePdfPreview src={referencePreview.layoutUrl} label={`${referencePreview.reference.name} page layout`} initialQuery={referencePreview.query} />
        : referencePreview.kind === "pdf" && referencePreview.url
        ? <MobilePdfPreview src={referencePreview.url} label={referencePreview.reference.name} initialQuery={referencePreview.query} />
        : referencePreview.kind === "docx" && referencePreview.docx
          ? <MobileDocxPreview reference={referencePreview.reference} document={referencePreview.docx} initialQuery={referencePreview.query} />
        : referencePreview.kind === "formatted" && referencePreview.docx
          ? <MobileSemanticDocumentPreview document={referencePreview.docx} initialQuery={referencePreview.query} />
        : referencePreview.kind === "workbook" && referencePreview.workbook
          ? <MobileWorkbookPreview workbook={referencePreview.workbook} initialQuery={referencePreview.query} initialSheet={referencePreview.focusSheet} initialRow={referencePreview.focusRow} />
          : <MobileTextReferencePreview text={referencePreview.text ?? ""} initialQuery={referencePreview.query} />}
    </section>}
  </main>;
}

function isConfirmed(value: string) {
  return ["validated", "approved", "signed_off", "protected"].includes(value);
}

function statusGlyph(segment: MobileSegment) {
  if (segment.locked) return "🔒";
  if (segment.review_status === "flagged") return "⚑";
  if (isConfirmed(segment.review_status)) return "✓";
  return "✎";
}

function containsExpectedTerm(text: string, term: string) {
  const trimmed = term.trim().replaceAll("\\'", "'");
  if (!trimmed) return false;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const startsWithWord = /^[\p{L}\p{N}]/u.test(trimmed);
  const endsWithWord = /[\p{L}\p{N}]$/u.test(trimmed);
  const pattern = `${startsWithWord ? "(?:^|[^\\p{L}\\p{N}])" : ""}${escaped}${endsWithWord ? "(?=$|[^\\p{L}\\p{N}])" : ""}`;
  return new RegExp(pattern, "iu").test(text.replaceAll("\\'", "'"));
}

function isMobileLongSegment(segment: MobileSegment) {
  const longest = Math.max(segment.source_text.length, segment.target.length);
  const sentences = Math.max(
    (segment.source_text.match(/[.!?…](?:[”»’"']?)(?=\s|$)/gu) ?? []).length,
    (segment.target.match(/[.!?…](?:[”»’"']?)(?=\s|$)/gu) ?? []).length,
  );
  return longest > 180 || sentences >= 3
    || Math.max((segment.source_text.match(/\n/g) ?? []).length, (segment.target.match(/\n/g) ?? []).length) >= 3;
}

type MobileTextChunk = { start: number; end: number; text: string };

function chunkMobileText(text: string, preferredLength = 230): MobileTextChunk[] {
  if (!text) return [{ start: 0, end: 0, text: "" }];
  const chunks: MobileTextChunk[] = [];
  let start = 0;
  while (start < text.length) {
    if (text.length - start <= preferredLength * 1.35) {
      chunks.push({ start, end: text.length, text: text.slice(start) });
      break;
    }
    const minimum = Math.min(text.length, start + Math.floor(preferredLength * .55));
    const maximum = Math.min(text.length, start + Math.floor(preferredLength * 1.35));
    const window = text.slice(minimum, maximum);
    let relative = -1;
    for (const pattern of [/\n+/gu, /[.!?…](?:[”»’"']?)[ \t]+/gu, /[;:](?:[”»’"']?)[ \t]+/gu, /[ \t]+/gu]) {
      for (const match of window.matchAll(pattern)) relative = (match.index ?? 0) + match[0].length;
      if (relative >= 0) break;
    }
    let end = relative >= 0 ? minimum + relative : maximum;
    const protectedTag = text.slice(start, end).match(/⟦(?:\/?T|R)\d*$/u);
    if (protectedTag?.index != null) end = start + protectedTag.index;
    if (end <= start) end = maximum;
    chunks.push({ start, end, text: text.slice(start, end) });
    start = end;
  }
  return chunks;
}

function MobileLongSegmentEditor({
  segment,
  language,
  terms,
  editable,
  normalize,
  onTagClick,
  onRequestEdit,
  onUndo,
  onRedo,
  onChange,
}: {
  segment: MobileSegment;
  language: string;
  terms: MobileJobBundle["companions"][string]["terms"];
  editable: boolean;
  normalize: (value: string) => string;
  onTagClick: (tag: string) => void;
  onRequestEdit: (position: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  onChange: (value: string) => void;
}) {
  const sourceChunks = useMemo(() => chunkMobileText(segment.source_text, 140), [segment.source_text]);
  const [targetChunks, setTargetChunks] = useState(() => chunkMobileText(segment.target, 140).map((item) => item.text));
  const host = useRef<HTMLDivElement | null>(null);
  const combined = targetChunks.join("");

  useEffect(() => {
    if (combined === segment.target || host.current?.contains(document.activeElement)) return;
    setTargetChunks(chunkMobileText(segment.target, 140).map((item) => item.text));
  }, [combined, segment.target]);

  const pairCount = Math.max(sourceChunks.length, targetChunks.length, 1);
  const offsets = targetChunks.map((_, index) => targetChunks.slice(0, index).join("").length);
  return <div ref={host} className="mobile-long-pairs" role="group" aria-label={`Segment ${segment.ordinal} displayed as ${pairCount} editing chunks; exported as one segment`}>
    {Array.from({ length: pairCount }, (_, index) => {
      const sourceChunk = sourceChunks[index]?.text ?? "";
      const targetChunk = targetChunks[index] ?? "";
      const offset = offsets[index] ?? combined.length;
      const chunkSegment = { ...segment, id: `${segment.id}--display-${index}`, source_text: sourceChunk, target: targetChunk };
      return <section className="mobile-long-pair" key={`${segment.id}-${index}`}>
        <header><span>Part {index + 1}/{pairCount}</span><small>one bilingual segment</small></header>
        <div id={`mobile-source-${segment.id}-${index}`} className="mobile-long-source" aria-label={`Source part ${index + 1}`}><p><HighlightedSource text={sourceChunk} target={segment.target} terms={terms} onTagClick={onTagClick} /></p></div>
        <div className="mobile-long-target" aria-label={`Target part ${index + 1}`}><MobileTargetEditor
          segment={chunkSegment}
          editable={editable}
          targetId={segment.id}
          targetOffset={offset}
          ariaLabel={`French target segment ${segment.ordinal}, part ${index + 1} of ${pairCount}`}
          describedBy={`mobile-source-${segment.id}-${index}`}
          language={language}
          normalize={normalize}
          onRequestEdit={onRequestEdit}
          onUndo={onUndo}
          onRedo={onRedo}
          onFocus={(editor) => {
            resizeMobileTargetField(editor);
            scrollMobileCaretIntoView(editor);
          }}
          onChange={(value, editor) => {
            setTargetChunks((current) => {
              const next = Array.from({ length: Math.max(pairCount, current.length) }, (_, chunkIndex) => current[chunkIndex] ?? "");
              next[index] = value;
              onChange(next.join(""));
              return next;
            });
            window.requestAnimationFrame(() => {
              resizeMobileTargetField(editor);
              scrollMobileCaretIntoView(editor);
            });
          }}
        /></div>
      </section>;
    })}
  </div>;
}

function MobileEditAccessory({ segment, tagInventory, terms, onInsertTag, onInsertTerm, onOpenTerms }: {
  segment: MobileSegment;
  tagInventory: Array<{ tag: string; present: boolean }>;
  terms: MobileJobBundle["companions"][string]["terms"];
  onInsertTag: (tag: string) => void;
  onInsertTerm: (term: string) => void;
  onOpenTerms: () => void;
}) {
  return <aside className="mobile-edit-accessory" aria-label="Target editing suggestions">
    {Boolean(tagInventory.length) && <div className="mobile-tag-status" aria-label="Source tags in order">{tagInventory.map((item, index) => <button className={item.present ? "present" : "missing"} key={`${item.tag}-${index}`} disabled={item.present} onPointerDown={(event) => event.preventDefault()} onClick={() => onInsertTag(item.tag)} aria-label={item.present ? `Tag ${item.tag} is already present` : `Missing tag ${item.tag}; insert at cursor`}><span>{tagDisplayLabel(item.tag)}</span></button>)}</div>}
    {Boolean(terms.length) && <div className="mobile-term-status" aria-label="Relevant terminology">{terms.map((term) => { const followed = Boolean(term.target_term && containsExpectedTerm(segment.target, term.target_term)); return <div className={`mobile-term-chip ${followed ? "followed" : "missing"}`} key={term.id}><button className="mobile-term-source" onPointerDown={(event) => event.preventDefault()} onClick={onOpenTerms} aria-label={`Open glossary details for ${term.source_term}`}><span>{term.source_term}</span><b>{followed ? "✓" : "!"}</b></button>{!followed && term.target_term && <button className="mobile-term-target" onPointerDown={(event) => event.preventDefault()} onClick={() => onInsertTerm(term.target_term!)} aria-label={`Insert French glossary term ${term.target_term} at the target cursor`} title={`Insert ${term.target_term} at the target cursor`}><small>→ {term.target_term}</small></button>}</div>; })}</div>}
  </aside>;
}

function MobileTargetEditor({
  segment,
  editable,
  targetId,
  targetOffset = 0,
  ariaLabel,
  describedBy,
  language,
  normalize,
  onRequestEdit,
  onUndo,
  onRedo,
  onFocus,
  onChange,
}: {
  segment: MobileSegment;
  editable: boolean;
  targetId?: string;
  targetOffset?: number;
  ariaLabel?: string;
  describedBy?: string;
  language: string;
  normalize: (value: string) => string;
  onRequestEdit: (position: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  onFocus: (editor: HTMLElement) => void;
  onChange: (value: string, editor: HTMLElement) => void;
}) {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor || serializeMobileEditor(editor) === segment.target) return;
    const selection = document.activeElement === editor ? mobileEditorSelection(editor) : null;
    populateMobileEditor(editor, segment.target);
    if (selection) setMobileEditorSelection(editor, selection.start, selection.end);
  }, [segment.target]);

  function emit(editor: HTMLDivElement) {
    const raw = serializeMobileEditor(editor);
    const selection = mobileEditorSelection(editor);
    const normalized = normalize(raw);
    const tokenCount = normalized.match(/⟦(?:\/?T|R)\d+⟧/g)?.length ?? 0;
    const renderedTokenCount = editor.querySelectorAll("[data-mobile-token]").length;
    const whitespaceCount = normalized.match(/[ \t\n\u00a0\u202f]/gu)?.length ?? 0;
    const renderedWhitespaceCount = editor.querySelectorAll("[data-mobile-whitespace]").length;
    if (normalized !== raw || tokenCount !== renderedTokenCount || whitespaceCount !== renderedWhitespaceCount) {
      const start = normalize(raw.slice(0, selection?.start ?? raw.length)).length;
      const end = normalize(raw.slice(0, selection?.end ?? raw.length)).length;
      populateMobileEditor(editor, normalized);
      setMobileEditorSelection(editor, start, end);
    }
    onChange(normalized, editor);
  }

  function replaceEditorSelection(editor: HTMLDivElement, replacement: string, forcedRange?: { start: number; end: number }) {
    const raw = serializeMobileEditor(editor);
    const selection = forcedRange ?? mobileEditorSelection(editor) ?? { start: raw.length, end: raw.length };
    const normalized = normalize(`${raw.slice(0, selection.start)}${replacement}${raw.slice(selection.end)}`);
    const caret = normalize(`${raw.slice(0, selection.start)}${replacement}`).length;
    populateMobileEditor(editor, normalized);
    setMobileEditorSelection(editor, caret, caret);
    onChange(normalized, editor);
  }

  function deleteEditorUnit(editor: HTMLDivElement, direction: "backward" | "forward") {
    const raw = serializeMobileEditor(editor);
    const selection = mobileEditorSelection(editor) ?? { start: raw.length, end: raw.length };
    if (selection.start !== selection.end) {
      replaceEditorSelection(editor, "", selection);
      return;
    }
    const tokenPattern = /⟦(?:\/?T|R)\d+⟧/g;
    for (const match of raw.matchAll(tokenPattern)) {
      const start = match.index ?? 0;
      const end = start + match[0].length;
      if ((direction === "backward" && end === selection.start) || (direction === "forward" && start === selection.start)) {
        replaceEditorSelection(editor, "", { start, end });
        return;
      }
    }
    if (direction === "backward" && selection.start > 0) {
      const previous = Array.from(raw.slice(0, selection.start)).at(-1) ?? "";
      replaceEditorSelection(editor, "", { start: selection.start - previous.length, end: selection.start });
    } else if (direction === "forward" && selection.start < raw.length) {
      const next = Array.from(raw.slice(selection.start))[0] ?? "";
      replaceEditorSelection(editor, "", { start: selection.start, end: selection.start + next.length });
    }
  }

  function copySelection(event: React.ClipboardEvent<HTMLDivElement>, cut: boolean) {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    event.preventDefault();
    event.clipboardData.setData("text/plain", serializeMobileEditor(range.cloneContents()));
    if (cut && editable && !segment.locked) {
      const selectedRange = mobileEditorSelection(editor);
      if (selectedRange) replaceEditorSelection(editor, "", selectedRange);
    }
  }

  return <div
    ref={(editor) => {
      editorRef.current = editor;
      if (editor && !editor.childNodes.length) populateMobileEditor(editor, segment.target);
    }}
    className="mobile-target-editor"
    data-mobile-target={targetId ?? segment.id}
    data-mobile-offset={targetOffset}
    aria-label={`${ariaLabel ?? `French target segment ${segment.ordinal}`}${segment.locked ? ", locked" : editable ? ", editing" : ", hold or double tap to edit"}`}
    aria-describedby={describedBy}
    lang={language}
    role="textbox"
    aria-multiline="true"
    aria-readonly={segment.locked || !editable}
    contentEditable={editable && !segment.locked}
    tabIndex={editable && !segment.locked ? 0 : -1}
    suppressContentEditableWarning
    spellCheck
    autoCapitalize="sentences"
    autoCorrect="on"
    onFocus={(event) => onFocus(event.currentTarget)}
    onDoubleClick={(event) => {
      if (!editable && !segment.locked) onRequestEdit(mobileEditorPointPosition(event.currentTarget, event.clientX, event.clientY));
    }}
    onBeforeInput={(event) => {
      if (!editable || segment.locked) { event.preventDefault(); return; }
      const input = event.nativeEvent as InputEvent;
      if (input.isComposing || input.inputType.includes("Composition")) return;
      if (input.inputType === "historyUndo" || input.inputType === "historyRedo") {
        // Use the string-level history instead of letting WebKit replay an
        // opaque DOM mutation over CAT tags and word spans.
        event.preventDefault();
        if (input.inputType === "historyRedo") onRedo();
        else onUndo();
        return;
      }
      if (input.inputType.startsWith("delete")) {
        event.preventDefault();
        deleteEditorUnit(event.currentTarget, input.inputType.toLocaleLowerCase().includes("forward") ? "forward" : "backward");
      } else if (input.inputType === "insertParagraph" || input.inputType === "insertLineBreak") {
        event.preventDefault();
        replaceEditorSelection(event.currentTarget, "\n");
      } else if (input.inputType === "insertText" && input.data != null) {
        event.preventDefault();
        replaceEditorSelection(event.currentTarget, input.data);
      }
    }}
    onInput={(event) => emit(event.currentTarget)}
    onCopy={(event) => copySelection(event, false)}
    onCut={(event) => copySelection(event, true)}
    onPaste={(event) => {
      if (segment.locked || !editable) return;
      event.preventDefault();
      replaceEditorSelection(event.currentTarget, event.clipboardData.getData("text/plain"));
    }}
  />;
}

function populateMobileEditor(editor: HTMLElement, value: string) {
  editor.replaceChildren();
  const pattern = /⟦(?:\/?T|R)\d+⟧/g;
  let cursor = 0;
  for (const match of value.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > cursor) appendMobileVisibleText(editor, value.slice(cursor, start));
    const tag = document.createElement("span");
    tag.className = "mobile-inline-tag mobile-editable-tag";
    tag.dataset.mobileToken = match[0];
    tag.contentEditable = "false";
    tag.textContent = tagDisplayLabel(match[0]);
    tag.title = match[0];
    editor.append(tag);
    cursor = start + match[0].length;
  }
  if (cursor < value.length) appendMobileVisibleText(editor, value.slice(cursor));
  if (!editor.childNodes.length) editor.append(document.createTextNode(""));
}

function appendMobileVisibleText(parent: HTMLElement, value: string) {
  let run: HTMLSpanElement | null = null;
  let normal = "";
  const ensureRun = () => {
    if (!run) { run = document.createElement("span"); run.className = "mobile-word-run"; }
    return run;
  };
  const flushText = () => {
    if (!normal) return;
    ensureRun().append(document.createTextNode(normal));
    normal = "";
  };
  const flushRun = () => {
    flushText();
    if (run?.childNodes.length) parent.append(run);
    run = null;
  };
  for (const character of value) {
    if (![" ", "\t", "\n", "\u00a0", "\u202f"].includes(character)) {
      normal += character;
      continue;
    }
    if (character === "\u00a0" || character === "\u202f") {
      flushText();
      ensureRun().append(createMobileWhitespaceMarker(character));
      continue;
    }
    flushRun();
    const marker = createMobileWhitespaceMarker(character);
    parent.append(marker);
    if (character === "\n") {
      const line = document.createElement("br");
      line.dataset.mobileVisualBreak = "true";
      parent.append(line);
    }
  }
  flushRun();
}

function createMobileWhitespaceMarker(character: string) {
    const marker = document.createElement("span");
    marker.className = `mobile-whitespace ${character === " " ? "space" : character === "\t" ? "tab" : character === "\n" ? "break" : "nonbreaking"}`;
    marker.dataset.mobileWhitespace = character;
    marker.contentEditable = "false";
    marker.setAttribute("aria-label", character === " " ? "space" : character === "\t" ? "tab" : character === "\n" ? "line break" : character === "\u202f" ? "narrow non-breaking space" : "non-breaking space");
    marker.textContent = character === "\t" ? "→" : character === "\n" ? "↵" : "·";
    return marker;
}

function serializeMobileEditor(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (node instanceof HTMLElement && node.dataset.mobileToken) return node.dataset.mobileToken;
  if (node instanceof HTMLElement && node.dataset.mobileWhitespace != null) return node.dataset.mobileWhitespace;
  if (node instanceof HTMLBRElement && node.dataset.mobileVisualBreak) return "";
  if (node instanceof HTMLBRElement) return "\n";
  let value = "";
  for (const child of Array.from(node.childNodes)) {
    const block = child instanceof HTMLElement && ["DIV", "P"].includes(child.tagName);
    if (block && value && !value.endsWith("\n")) value += "\n";
    value += serializeMobileEditor(child);
  }
  return value;
}

function mobileEditorSelection(editor: HTMLElement) {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return null;
  const beforeStart = document.createRange();
  beforeStart.selectNodeContents(editor);
  beforeStart.setEnd(range.startContainer, range.startOffset);
  const beforeEnd = document.createRange();
  beforeEnd.selectNodeContents(editor);
  beforeEnd.setEnd(range.endContainer, range.endOffset);
  return { start: serializeMobileEditor(beforeStart.cloneContents()).length, end: serializeMobileEditor(beforeEnd.cloneContents()).length };
}

function mobileEditorPointPosition(editor: HTMLElement, x: number, y: number) {
  const documentWithCaret = document as Document & {
    caretPositionFromPoint?: (clientX: number, clientY: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (clientX: number, clientY: number) => Range | null;
  };
  const caret = documentWithCaret.caretPositionFromPoint?.(x, y);
  const fallback = caret ? null : documentWithCaret.caretRangeFromPoint?.(x, y);
  const node = caret?.offsetNode ?? fallback?.startContainer ?? null;
  const offset = caret?.offset ?? fallback?.startOffset ?? 0;
  if (!node || (node !== editor && !editor.contains(node))) {
    return Number(editor.dataset.mobileOffset ?? 0) + serializeMobileEditor(editor).length;
  }
  const before = document.createRange();
  before.selectNodeContents(editor);
  try { before.setEnd(node, offset); }
  catch { return Number(editor.dataset.mobileOffset ?? 0) + serializeMobileEditor(editor).length; }
  return Number(editor.dataset.mobileOffset ?? 0) + serializeMobileEditor(before.cloneContents()).length;
}

function setMobileEditorSelection(editor: HTMLElement, start: number, end: number) {
  const selection = window.getSelection();
  if (!selection) return;
  const startPoint = mobileEditorBoundary(editor, start);
  const endPoint = mobileEditorBoundary(editor, end);
  const range = document.createRange();
  range.setStart(startPoint.node, startPoint.offset);
  range.setEnd(endPoint.node, endPoint.offset);
  selection.removeAllRanges();
  selection.addRange(range);
}

function mobileTargetEditors(segmentId: string) {
  return [...document.querySelectorAll<HTMLElement>("[data-mobile-target]")].filter((item) => item.dataset.mobileTarget === segmentId);
}

function activeMobileTargetEditor(segmentId: string) {
  const active = document.activeElement;
  if (active instanceof HTMLElement && active.dataset.mobileTarget === segmentId) return active;
  return mobileTargetEditors(segmentId)[0] ?? null;
}

function focusMobileTargetPosition(segmentId: string, position: number) {
  const editors = mobileTargetEditors(segmentId);
  const editor = editors.find((candidate) => {
    const start = Number(candidate.dataset.mobileOffset ?? 0);
    return position >= start && position <= start + serializeMobileEditor(candidate).length;
  }) ?? editors.at(-1) ?? null;
  if (!editor) return null;
  const offset = Number(editor.dataset.mobileOffset ?? 0);
  const local = Math.max(0, Math.min(serializeMobileEditor(editor).length, position - offset));
  editor.focus({ preventScroll: true });
  setMobileEditorSelection(editor, local, local);
  window.requestAnimationFrame(() => scrollMobileCaretIntoView(editor));
  return editor;
}

function scrollMobileCaretIntoView(editor: HTMLElement) {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editor.contains(selection.getRangeAt(0).commonAncestorContainer)) return;
  const scroller = editor.closest<HTMLElement>(".mobile-segment-scroll");
  if (!scroller) return;
  const adjust = () => {
    if (!document.contains(editor)) return;
    const current = window.getSelection();
    if (!current?.rangeCount || !editor.contains(current.getRangeAt(0).commonAncestorContainer)) return;
    const caret = mobileCaretBounds(current.getRangeAt(0), editor);
    const scrollRect = scroller.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
    const visibleTop = Math.max(scrollRect.top, viewportTop) + 12;
    const visibleBottom = Math.min(scrollRect.bottom, viewportBottom) - 12;
    if (caret.top < visibleTop) scroller.scrollTop += caret.top - visibleTop;
    else if (caret.bottom > visibleBottom) scroller.scrollTop += caret.bottom - visibleBottom;
  };
  adjust();
  window.requestAnimationFrame(adjust);
}

function resizeMobileTargetField(editor: HTMLElement | null) {
  if (!editor) return;
  const editorFont = Number.parseFloat(window.getComputedStyle(editor).fontSize) || 16;
  editor.style.minHeight = `${Math.max(Math.ceil(editorFont * 1.55), editor.scrollHeight)}px`;
}

function mobileCaretBounds(range: Range, editor: HTMLElement) {
  const direct = range.getBoundingClientRect();
  if (direct.height || direct.width) return direct;
  const nodeRect = (node: Node | undefined) => {
    if (!node) return null;
    if (node instanceof Element) return node.getBoundingClientRect();
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.length) {
      const probe = document.createRange();
      probe.selectNodeContents(node);
      const offset = node === range.startContainer ? range.startOffset : node.textContent.length;
      if (offset < node.textContent.length) {
        probe.setStart(node, offset);
        probe.setEnd(node, offset + 1);
      } else {
        probe.setStart(node, Math.max(0, offset - 1));
        probe.setEnd(node, offset);
      }
      return probe.getBoundingClientRect();
    }
    return null;
  };
  if (range.startContainer instanceof Element) {
    const children = range.startContainer.childNodes;
    const next = nodeRect(children[range.startOffset]);
    if (next?.height || next?.width) return next;
    const previous = nodeRect(children[range.startOffset - 1]);
    if (previous?.height || previous?.width) return previous;
  } else {
    const text = nodeRect(range.startContainer);
    if (text?.height || text?.width) return text;
  }
  return editor.getBoundingClientRect();
}

function mobileEditorBoundary(root: HTMLElement, wanted: number): { node: Node; offset: number } {
  let remaining = Math.max(0, wanted);
  const visit = (node: Node): { node: Node; offset: number } | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      const length = node.textContent?.length ?? 0;
      if (remaining <= length) return { node, offset: remaining };
      remaining -= length;
      return null;
    }
    if (node instanceof HTMLElement && (node.dataset.mobileToken || node.dataset.mobileWhitespace != null)) {
      const length = node.dataset.mobileToken?.length ?? node.dataset.mobileWhitespace?.length ?? 0;
      const parent = node.parentNode!;
      const index = Array.prototype.indexOf.call(parent.childNodes, node);
      if (remaining <= length) return { node: parent, offset: index + (remaining > length / 2 ? 1 : 0) };
      remaining -= length;
      return null;
    }
    if (node instanceof HTMLBRElement && node.dataset.mobileVisualBreak) return null;
    if (node instanceof HTMLBRElement) {
      if (remaining <= 1) return { node: node.parentNode!, offset: Array.prototype.indexOf.call(node.parentNode!.childNodes, node) + 1 };
      remaining -= 1;
      return null;
    }
    for (const child of Array.from(node.childNodes)) {
      const found = visit(child);
      if (found) return found;
    }
    return null;
  };
  return visit(root) ?? { node: root, offset: root.childNodes.length };
}

function tagDisplayLabel(token: string) {
  const match = token.match(/^⟦(\/?[TR])(\d+)⟧$/);
  return match ? `${match[1]}${Number(match[2]) + 1}` : token;
}

function HighlightedSource({ text, target, terms, onTagClick }: { text: string; target: string; terms: MobileJobBundle["companions"][string]["terms"]; onTagClick?: (tag: string) => void }) {
  const candidates: Array<{ start: number; end: number; term: MobileJobBundle["companions"][string]["terms"][number]; followed: boolean }> = [];
  for (const term of terms) {
    const source = term.source_term.trim();
    if (!source) continue;
    const regex = new RegExp(source.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "giu");
    for (const match of text.matchAll(regex)) {
      const start = match.index ?? 0;
      const end = start + match[0].length;
      if (/^[\p{L}\p{N}]/u.test(source) && start > 0 && /[\p{L}\p{N}]/u.test(text[start - 1])) continue;
      if (/[\p{L}\p{N}]$/u.test(source) && end < text.length && /[\p{L}\p{N}]/u.test(text[end])) continue;
      candidates.push({ start, end, term, followed: Boolean(term.target_term && containsExpectedTerm(target, term.target_term)) });
    }
  }
  candidates.sort((left, right) => left.start - right.start || right.end - right.start - (left.end - left.start));
  const accepted = candidates.filter((candidate, index, all) => !all.slice(0, index).some((earlier) => candidate.start < earlier.end && candidate.end > earlier.start));
  if (!accepted.length) return <>{renderInlineTags(text, "source", onTagClick)}</>;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  for (const item of accepted) {
    if (item.start > cursor) nodes.push(...renderInlineTags(text.slice(cursor, item.start), `before-${item.start}`, onTagClick));
    nodes.push(<mark className={item.followed ? "mobile-term-followed" : "mobile-term-missing"} title={`${item.term.source_term} → ${item.term.target_term ?? "missing French term"}`} key={`${item.term.id}-${item.start}`}>{renderInlineTags(text.slice(item.start, item.end), `term-${item.start}`, onTagClick)}</mark>);
    cursor = item.end;
  }
  if (cursor < text.length) nodes.push(...renderInlineTags(text.slice(cursor), `after-${cursor}`, onTagClick));
  return <>{nodes}</>;
}

function MobileTextReferencePreview({ text, initialQuery }: { text: string; initialQuery: string }) {
  const [query, setQuery] = useState(initialQuery);
  const content = useRef<HTMLDivElement | null>(null);
  const plan = useMemo(() => portableReferenceMatchPlan(text, query), [text, query]);

  useEffect(() => {
    const target = content.current?.querySelector<HTMLElement>("mark[data-reference-focus='true']");
    target?.scrollIntoView({ block: "center", inline: "nearest" });
  }, [plan.focus]);

  return <div className="mobile-text-reference">
    <ReferenceSearchBar value={query} onChange={setQuery} result={`${plan.ranges.length} hit${plan.ranges.length === 1 ? "" : "s"}`} />
    <div ref={content} className="mobile-text-reference-content" lang="und">{renderReferenceMatches(text, plan)}</div>
  </div>;
}

function MobileDocxPreview({ reference, document, initialQuery }: { reference: MobileReferenceFile; document: PortableDocxPreview; initialQuery: string }) {
  const [query, setQuery] = useState(initialQuery);
  const [renderState, setRenderState] = useState<"loading" | "ready" | "fallback">("loading");
  const [renderRevision, setRenderRevision] = useState(0);
  const [zoom, setZoom] = useState(1);
  const viewport = useRef<HTMLDivElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);
  const styles = useRef<HTMLDivElement | null>(null);
  const plan = useMemo(() => portableReferenceMatchPlan(document.text, query), [document.text, query]);

  useEffect(() => {
    let cancelled = false;
    const root = content.current;
    const styleRoot = styles.current;
    if (!root || !styleRoot) return;
    setRenderState("loading");
    root.replaceChildren();
    styleRoot.replaceChildren();
    void import("docx-preview").then(async ({ renderAsync }) => {
      const bytes = portableReferenceBytes(reference);
      const input = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      await renderAsync(input, root, styleRoot, {
        className: "tcc-docx",
        inWrapper: true,
        breakPages: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        ignoreLastRenderedPageBreak: false,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true,
        renderChanges: false,
        renderComments: false,
        renderAltChunks: false,
        useBase64URL: true,
        debug: false,
      });
      if (cancelled) return;
      hardenRenderedReference(root);
      const page = root.querySelector<HTMLElement>("section.tcc-docx");
      const available = Math.max(1, (viewport.current?.clientWidth ?? window.innerWidth) - 12);
      const naturalWidth = page?.offsetWidth || page?.scrollWidth || available;
      setZoom(Math.max(.28, Math.min(1, available / naturalWidth)));
      setRenderState("ready");
      setRenderRevision((value) => value + 1);
    }).catch(() => {
      if (cancelled) return;
      root.innerHTML = document.html;
      setZoom(1);
      setRenderState("fallback");
      setRenderRevision((value) => value + 1);
    });
    return () => { cancelled = true; };
  }, [document.html, reference.content_base64]);

  useEffect(() => {
    const root = content.current;
    if (!root || renderState === "loading") return;
    clearReferenceMarks(root);
    if (!query.trim()) return;
    const phrase = cleanReferenceQuery(query).toLocaleLowerCase();
    const terms = referenceSearchTerms(query).map((term) => term.toLocaleLowerCase());
    const blocks = [...root.querySelectorAll<HTMLElement>("h1,h2,h3,h4,h5,h6,p,li,td,th,blockquote,figcaption")];
    const bestBlock = blocks.map((block) => {
      const value = cleanReferenceQuery(block.textContent ?? "").toLocaleLowerCase();
      const exact = phrase && value.includes(phrase) ? 10000 + phrase.length : 0;
      const termScore = terms.reduce((total, term) => total + (value.includes(term) ? term.length : 0), 0);
      return { block, score: exact + termScore };
    }).sort((left, right) => right.score - left.score)[0]?.block ?? root;
    let focused = false;
    let rendered = 0;
    const walker = window.document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    let current: Node | null = walker.nextNode();
    while (current) {
      if (current.nodeValue?.trim()) nodes.push(current as Text);
      current = walker.nextNode();
    }
    for (const node of nodes) {
      if (rendered >= 500) break;
      const matchPlan = portableReferenceMatchPlan(node.nodeValue ?? "", query);
      if (!matchPlan.ranges.length) continue;
      const fragment = window.document.createDocumentFragment();
      let cursor = 0;
      for (const range of matchPlan.ranges) {
        if (range.start > cursor) fragment.append(node.nodeValue!.slice(cursor, range.start));
        const mark = window.document.createElement("mark");
        mark.textContent = node.nodeValue!.slice(range.start, range.end);
        mark.dataset.tccReferenceMark = "true";
        if (!focused && bestBlock.contains(node)) {
          mark.dataset.referenceFocus = "true";
          focused = true;
        }
        fragment.append(mark);
        cursor = range.end;
        rendered += 1;
        if (rendered >= 500) break;
      }
      if (cursor < node.nodeValue!.length) fragment.append(node.nodeValue!.slice(cursor));
      node.replaceWith(fragment);
    }
    (root.querySelector<HTMLElement>("mark[data-reference-focus='true']") ?? bestBlock)?.scrollIntoView({ block: "center", inline: "nearest" });
  }, [query, renderRevision, renderState]);

  function fitWordPage() {
    const root = content.current;
    const page = root?.querySelector<HTMLElement>("section.tcc-docx");
    const available = Math.max(1, (viewport.current?.clientWidth ?? window.innerWidth) - 12);
    const renderedWidth = page?.getBoundingClientRect().width ?? available;
    const naturalWidth = renderedWidth / Math.max(zoom, .01);
    setZoom(Math.max(.28, Math.min(1, available / naturalWidth)));
  }

  return <div className={`mobile-docx-preview ${renderState === "fallback" ? "semantic-fallback" : "native-pages"}`}>
    <ReferenceSearchBar value={query} onChange={setQuery} result={`${plan.ranges.length} hit${plan.ranges.length === 1 ? "" : "s"}`} />
    {renderState !== "fallback" && <nav aria-label="Word document zoom controls">
      <button onClick={() => setZoom((value) => Math.max(.28, value - .1))} disabled={zoom <= .28} aria-label="Zoom Word document out"><Minus /></button>
      <output>{renderState === "loading" ? "Opening…" : `${Math.round(zoom * 100)}%`}</output>
      <button onClick={() => setZoom((value) => Math.min(2.5, value + .1))} disabled={zoom >= 2.5} aria-label="Zoom Word document in"><Plus /></button>
      <button onClick={fitWordPage} disabled={renderState !== "ready"} aria-label="Fit Word page to screen">Fit</button>
    </nav>}
    <div ref={styles} className="mobile-docx-style-host" aria-hidden="true" />
    <div ref={viewport} className="mobile-docx-viewport" lang="und" aria-busy={renderState === "loading"}>
      <div ref={content} className="mobile-docx-content" style={{ "--mobile-docx-scale": zoom } as CSSProperties} dangerouslySetInnerHTML={renderState === "fallback" ? { __html: document.html } : undefined} />
    </div>
  </div>;
}

function MobileSemanticDocumentPreview({ document, initialQuery }: { document: PortableDocxPreview; initialQuery: string }) {
  const [query, setQuery] = useState(initialQuery);
  const content = useRef<HTMLDivElement | null>(null);
  const plan = useMemo(() => portableReferenceMatchPlan(document.text, query), [document.text, query]);

  useEffect(() => {
    const root = content.current;
    if (!root) return;
    root.innerHTML = document.html;
    if (!query.trim()) return;
    const phrase = cleanReferenceQuery(query).toLocaleLowerCase();
    const terms = referenceSearchTerms(query).map((term) => term.toLocaleLowerCase());
    const blocks = [...root.querySelectorAll<HTMLElement>("h1,h2,h3,h4,h5,h6,p,li,td,th,blockquote,figcaption")];
    const bestBlock = blocks.map((block) => {
      const value = cleanReferenceQuery(block.textContent ?? "").toLocaleLowerCase();
      const exact = phrase && value.includes(phrase) ? 10000 + phrase.length : 0;
      const termScore = terms.reduce((total, term) => total + (value.includes(term) ? term.length : 0), 0);
      return { block, score: exact + termScore };
    }).sort((left, right) => right.score - left.score)[0]?.block ?? root;
    let focused = false;
    let rendered = 0;
    const walker = window.document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    let current: Node | null = walker.nextNode();
    while (current) {
      if (current.nodeValue?.trim()) nodes.push(current as Text);
      current = walker.nextNode();
    }
    for (const node of nodes) {
      if (rendered >= 500) break;
      const matchPlan = portableReferenceMatchPlan(node.nodeValue ?? "", query);
      if (!matchPlan.ranges.length) continue;
      const fragment = window.document.createDocumentFragment();
      let cursor = 0;
      for (const range of matchPlan.ranges) {
        if (range.start > cursor) fragment.append(node.nodeValue!.slice(cursor, range.start));
        const mark = window.document.createElement("mark");
        mark.textContent = node.nodeValue!.slice(range.start, range.end);
        if (!focused && bestBlock.contains(node)) { mark.dataset.referenceFocus = "true"; focused = true; }
        fragment.append(mark);
        cursor = range.end;
        rendered += 1;
        if (rendered >= 500) break;
      }
      if (cursor < node.nodeValue!.length) fragment.append(node.nodeValue!.slice(cursor));
      node.replaceWith(fragment);
    }
    (root.querySelector<HTMLElement>("mark[data-reference-focus='true']") ?? bestBlock)?.scrollIntoView({ block: "center", inline: "nearest" });
  }, [document.html, query]);

  return <div className="mobile-docx-preview semantic-fallback">
    <ReferenceSearchBar value={query} onChange={setQuery} result={`${plan.ranges.length} hit${plan.ranges.length === 1 ? "" : "s"}`} />
    <div ref={content} className="mobile-docx-content" lang="und" dangerouslySetInnerHTML={{ __html: document.html }} />
  </div>;
}

function clearReferenceMarks(root: HTMLElement) {
  for (const mark of root.querySelectorAll<HTMLElement>("mark[data-tcc-reference-mark='true']")) mark.replaceWith(window.document.createTextNode(mark.textContent ?? ""));
  root.normalize();
}

function hardenRenderedReference(root: HTMLElement) {
  for (const element of root.querySelectorAll<HTMLElement>("*")) {
    for (const attribute of [...element.attributes]) if (/^on/iu.test(attribute.name)) element.removeAttribute(attribute.name);
  }
  for (const link of root.querySelectorAll<HTMLAnchorElement>("a[href]")) {
    const href = link.getAttribute("href")?.trim() ?? "";
    if (/^(?:javascript|data):/iu.test(href)) link.removeAttribute("href");
    else if (/^https?:/iu.test(href)) { link.target = "_blank"; link.rel = "noopener noreferrer"; }
  }
}

function MobileWorkbookPreview({ workbook, initialQuery, initialSheet, initialRow }: { workbook: PortableWorkbookGrid; initialQuery: string; initialSheet?: string | null; initialRow?: number | null }) {
  const initialFocus = useMemo(() => mobileWorkbookFocus(workbook, initialQuery, initialSheet, initialRow), [workbook, initialQuery, initialSheet, initialRow]);
  const [query, setQuery] = useState(initialQuery);
  const [sheetName, setSheetName] = useState(initialFocus.sheetName ?? workbook.sheets[0]?.name ?? "");
  const [zoom, setZoom] = useState(.85);
  const viewport = useRef<HTMLDivElement | null>(null);
  const search = useMemo(() => mobileWorkbookFocus(workbook, query), [workbook, query]);
  const activeSheet = workbook.sheets.find((item) => item.name === sheetName) ?? workbook.sheets[0];
  const initialQueryRef = useRef(initialQuery);

  useEffect(() => {
    if (query !== initialQueryRef.current && search.sheetName) setSheetName(search.sheetName);
  }, [query, search.sheetName]);

  const focusRow = query === initialQueryRef.current && initialFocus.sheetName === sheetName ? initialFocus.rowNumber : search.sheetName === sheetName ? search.rowNumber : null;
  const focusCoordinate = query === initialQueryRef.current && initialFocus.sheetName === sheetName ? initialFocus.coordinate : search.sheetName === sheetName ? search.coordinate : null;
  useEffect(() => {
    const selector = focusCoordinate ? `[data-workbook-cell='${focusCoordinate}']` : focusRow ? `[data-workbook-row='${focusRow}']` : "";
    if (!selector) return;
    window.setTimeout(() => viewport.current?.querySelector<HTMLElement>(selector)?.scrollIntoView({ block: "center", inline: "center" }), 0);
  }, [focusCoordinate, focusRow, sheetName]);

  if (!activeSheet) return <p>This workbook has no visible cells.</p>;
  return <div className="mobile-workbook-preview">
    <ReferenceSearchBar value={query} onChange={setQuery} result={`${search.hits} hit${search.hits === 1 ? "" : "s"}`} />
    <nav aria-label="Workbook sheet and zoom controls">
      <select value={activeSheet.name} onChange={(event) => setSheetName(event.target.value)} aria-label="Workbook sheet">{workbook.sheets.map((sheet) => <option key={sheet.name} value={sheet.name}>{sheet.name}</option>)}</select>
      <button onClick={() => setZoom((value) => Math.max(.5, value - .1))} disabled={zoom <= .5} aria-label="Zoom workbook out"><Minus /></button>
      <output>{Math.round(zoom * 100)}%</output>
      <button onClick={() => setZoom((value) => Math.min(2, value + .1))} disabled={zoom >= 2} aria-label="Zoom workbook in"><Plus /></button>
    </nav>
    <div ref={viewport} className="mobile-workbook-grid" style={{ "--mobile-workbook-scale": zoom } as CSSProperties}>
      <table>
        <thead><tr><th aria-label="Row number" />{Array.from({ length: activeSheet.columnCount }, (_, index) => <th key={index}>{spreadsheetColumnLabel(activeSheet.startColumn + index)}</th>)}</tr></thead>
        <tbody>{activeSheet.rows.map((row) => {
          const cells = new Map(row.cells.map((cell) => [cell.columnIndex, cell]));
          return <tr key={row.rowNumber} data-workbook-row={row.rowNumber} className={row.rowNumber === focusRow ? "focused-row" : undefined}>
            <th>{row.rowNumber}</th>
            {Array.from({ length: activeSheet.columnCount }, (_, index) => {
              const column = activeSheet.startColumn + index;
              const cell = cells.get(column);
              if (!cell) return <td key={column} />;
              const plan = portableReferenceMatchPlan(cell.value, query);
              const focused = cell.coordinate === focusCoordinate;
              return <td key={column} data-workbook-cell={cell.coordinate} className={focused ? "focused-cell" : undefined} title={`${cell.coordinate}: ${cell.value}`}>{renderReferenceMatches(cell.value, plan)}</td>;
            })}
          </tr>;
        })}</tbody>
      </table>
      {workbook.truncated && <p className="mobile-workbook-limit">This exceptionally large workbook is capped in the phone grid. Searchable mapped rows remain available in the reference companion.</p>}
    </div>
  </div>;
}

function mobileWorkbookFocus(workbook: PortableWorkbookGrid, query: string, requestedSheet?: string | null, requestedRow?: number | null) {
  if (requestedSheet && requestedRow) {
    const sheet = workbook.sheets.find((item) => item.name === requestedSheet);
    const row = sheet?.rows.find((item) => item.rowNumber === requestedRow);
    if (sheet && row) {
      const matching = [...row.cells]
        .map((cell) => ({ cell, score: mobileWorkbookCellScore(cell.value, query) }))
        .sort((left, right) => right.score - left.score)[0];
      return { sheetName: sheet.name, rowNumber: row.rowNumber, coordinate: matching?.cell.coordinate ?? row.cells[0]?.coordinate ?? null, hits: countMobileWorkbookHits(workbook, query) };
    }
  }
  let best: { sheetName: string; rowNumber: number; coordinate: string; score: number } | null = null;
  let hits = 0;
  for (const sheet of workbook.sheets) {
    for (const row of sheet.rows) {
      for (const cell of row.cells) {
        const plan = portableReferenceMatchPlan(cell.value, query);
        hits += plan.ranges.length;
        if (!plan.ranges.length) continue;
        const score = mobileWorkbookCellScore(cell.value, query);
        if (!best || score > best.score) best = { sheetName: sheet.name, rowNumber: row.rowNumber, coordinate: cell.coordinate, score };
      }
    }
  }
  return { sheetName: best?.sheetName ?? workbook.sheets[0]?.name ?? null, rowNumber: best?.rowNumber ?? null, coordinate: best?.coordinate ?? null, hits };
}

function mobileWorkbookCellScore(value: string, query: string) {
  const normalized = cleanReferenceQuery(value).toLocaleLowerCase();
  const phrase = cleanReferenceQuery(query).toLocaleLowerCase();
  if (phrase && normalized.includes(phrase)) return 10_000 + phrase.length;
  return referenceSearchTerms(phrase).reduce((total, term) => total + (normalized.includes(term.toLocaleLowerCase()) ? term.length : 0), 0);
}

function countMobileWorkbookHits(workbook: PortableWorkbookGrid, query: string) {
  let hits = 0;
  for (const sheet of workbook.sheets) for (const row of sheet.rows) for (const cell of row.cells) hits += portableReferenceMatchPlan(cell.value, query).ranges.length;
  return hits;
}

function spreadsheetColumnLabel(column: number) {
  let value = column + 1;
  let label = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

function MobilePdfPreview({ src, label, initialQuery }: { src: string; label: string; initialQuery: string }) {
  const host = useRef<HTMLDivElement | null>(null);
  const [hostWidth, setHostWidth] = useState(300);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [query, setQuery] = useState(initialQuery);
  const [pageTexts, setPageTexts] = useState<string[]>([]);
  const [indexing, setIndexing] = useState(true);
  const search = useMemo(() => portablePdfSearch(pageTexts, query), [pageTexts, query]);
  const lastAutoSearch = useRef("");

  useEffect(() => {
    const element = host.current;
    if (!element) return;
    const measure = () => setHostWidth(Math.max(240, element.clientWidth - 12));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadingTask = pdfjs.getDocument(src);
    setIndexing(true);
    void loadingTask.promise.then(async (document) => {
      const text: string[] = [];
      for (let index = 1; index <= document.numPages; index += 1) {
        if (cancelled) return;
        const current = await document.getPage(index);
        const layer = await current.getTextContent();
        text.push(layer.items.map((item) => "str" in item ? item.str : "").join(" "));
      }
      if (!cancelled) {
        setPageTexts(text);
        setIndexing(false);
      }
    }).catch(() => { if (!cancelled) setIndexing(false); });
    return () => {
      cancelled = true;
      void loadingTask.destroy();
    };
  }, [src]);

  useEffect(() => {
    const key = `${query}\u0000${pageTexts.length}`;
    if (lastAutoSearch.current === key) return;
    lastAutoSearch.current = key;
    if (search.bestPage) setPage(search.bestPage);
  }, [pageTexts.length, query, search.bestPage]);

  return <div className="mobile-pdf-preview" ref={host}>
    <ReferenceSearchBar value={query} onChange={setQuery} result={indexing ? "Indexing…" : `${search.pages.length} page${search.pages.length === 1 ? "" : "s"}`} />
    <nav aria-label="PDF page and zoom controls">
      <button onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1} aria-label="Previous PDF page">‹</button>
      <output>{pages ? `${page}/${pages}` : "…"}</output>
      <button onClick={() => setPage((value) => Math.min(pages || 1, value + 1))} disabled={!pages || page >= pages} aria-label="Next PDF page">›</button>
      <button onClick={() => setZoom((value) => Math.max(.75, value - .25))} disabled={zoom <= .75} aria-label="Zoom PDF out"><Minus /></button>
      <output>{Math.round(zoom * 100)}%</output>
      <button onClick={() => setZoom((value) => Math.min(2.5, value + .25))} disabled={zoom >= 2.5} aria-label="Zoom PDF in"><Plus /></button>
    </nav>
    <div className="mobile-pdf-canvas">
      <Document file={src} onLoadSuccess={({ numPages }) => { setPages(numPages); setPage((value) => Math.min(value, numPages)); }} loading={<p>Opening PDF…</p>} error={<p>This PDF could not be rendered here. Use the external-viewer button above.</p>}>
        <Page pageNumber={page} width={Math.round(hostWidth * zoom)} renderTextLayer renderAnnotationLayer customTextRenderer={({ str }) => highlightReferenceHtml(str, query)} aria-label={`${label}, page ${page}`} />
      </Document>
    </div>
  </div>;
}

function ReferenceSearchBar({ value, onChange, result }: { value: string; onChange: (value: string) => void; result: string }) {
  return <label className="mobile-reference-search"><Search /><span className="mobile-visually-hidden">Search this reference</span><input value={value} onChange={(event) => onChange(event.target.value)} placeholder="Find current string…" inputMode="search" /><output>{result}</output>{value && <button type="button" onClick={() => onChange("")} aria-label="Clear reference search"><X /></button>}</label>;
}

type PortableReferenceMatchPlan = { ranges: Array<{ start: number; end: number }>; focus: number };

function portableReferenceMatchPlan(text: string, query: string): PortableReferenceMatchPlan {
  const phrase = cleanReferenceQuery(query);
  if (!phrase) return { ranges: [], focus: -1 };
  const exact = collectReferenceRanges(text, phrase.split(/\s+/u).map(escapeRegularExpression).join("\\s+"));
  if (exact.length) return { ranges: exact, focus: exact[0].start };
  const terms = referenceSearchTerms(phrase);
  if (!terms.length) return { ranges: [], focus: -1 };
  const ranges = collectReferenceRanges(text, terms.map(escapeRegularExpression).join("|"));
  let focus = ranges[0]?.start ?? -1;
  let best = -1;
  let offset = 0;
  for (const block of text.split(/\n{2,}/u)) {
    const normalized = block.toLocaleLowerCase();
    const score = terms.reduce((total, term) => total + (normalized.includes(term.toLocaleLowerCase()) ? 1 : 0), 0);
    if (score > best) {
      const first = ranges.find((range) => range.start >= offset && range.start < offset + block.length);
      if (first) { best = score; focus = first.start; }
    }
    offset += block.length + 2;
  }
  return { ranges, focus };
}

function renderReferenceMatches(text: string, plan: PortableReferenceMatchPlan) {
  if (!plan.ranges.length) return text;
  const output: React.ReactNode[] = [];
  let cursor = 0;
  for (const [index, range] of plan.ranges.entries()) {
    if (range.start > cursor) output.push(text.slice(cursor, range.start));
    output.push(<mark key={`${range.start}-${range.end}`} data-reference-focus={range.start === plan.focus ? "true" : undefined}>{text.slice(range.start, range.end)}</mark>);
    cursor = range.end;
    if (index >= 499) break;
  }
  if (cursor < text.length) output.push(text.slice(cursor));
  return output;
}

function portablePdfSearch(pageTexts: string[], query: string) {
  const phrase = cleanReferenceQuery(query);
  if (!phrase) return { pages: [] as number[], bestPage: 0 };
  const normalizedPhrase = phrase.toLocaleLowerCase();
  const exactPages = pageTexts.map((text, index) => ({ page: index + 1, text: cleanReferenceQuery(text).toLocaleLowerCase() })).filter((item) => item.text.includes(normalizedPhrase)).map((item) => item.page);
  if (exactPages.length) return { pages: exactPages, bestPage: exactPages[0] };
  const terms = referenceSearchTerms(phrase);
  const scored = pageTexts.map((text, index) => {
    const normalized = text.toLocaleLowerCase();
    return { page: index + 1, score: terms.reduce((total, term) => total + (normalized.includes(term.toLocaleLowerCase()) ? 1 : 0), 0) };
  }).filter((item) => item.score > 0);
  const best = [...scored].sort((left, right) => right.score - left.score || left.page - right.page)[0];
  return { pages: scored.map((item) => item.page), bestPage: best?.page ?? 0 };
}

function highlightReferenceHtml(text: string, query: string) {
  const terms = referenceSearchTerms(cleanReferenceQuery(query));
  if (!terms.length) return escapeHtml(text);
  const matcher = new RegExp(terms.map(escapeRegularExpression).join("|"), "giu");
  let cursor = 0;
  let output = "";
  for (const match of text.matchAll(matcher)) {
    const start = match.index ?? 0;
    output += escapeHtml(text.slice(cursor, start));
    output += `<mark>${escapeHtml(match[0])}</mark>`;
    cursor = start + match[0].length;
  }
  return output + escapeHtml(text.slice(cursor));
}

function collectReferenceRanges(text: string, expression: string) {
  if (!expression) return [];
  const ranges: Array<{ start: number; end: number }> = [];
  const matcher = new RegExp(expression, "giu");
  for (const match of text.matchAll(matcher)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    const previous = ranges.at(-1);
    if (!previous || start >= previous.end) ranges.push({ start, end });
    if (ranges.length >= 500) break;
  }
  return ranges;
}

function cleanReferenceQuery(value: string) {
  return value.replace(/⟦(?:\/?T|R)\d+⟧/g, "").normalize("NFKC").replace(/\s+/gu, " ").trim();
}

function referenceSearchTerms(value: string) {
  const ignored = new Set(["the", "and", "for", "with", "from", "that", "this", "your", "you", "are", "not", "les", "des", "une", "dans", "pour", "avec", "sur", "est"]);
  return [...new Set(value.match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu) ?? [])]
    .filter((term) => term.length >= 3 && !ignored.has(term.toLocaleLowerCase()))
    .sort((left, right) => right.length - left.length)
    .slice(0, 10);
}

function escapeRegularExpression(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeHtml(value: string) {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

function MobileZoomImage({ src, alt, onOpen }: { src: string; alt: string; onOpen: () => void }) {
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [loadFailed, setLoadFailed] = useState(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, distance: 0, scale: 1 });
  const clampScale = (value: number) => Math.min(5, Math.max(1, value));

  useEffect(() => {
    setLoadFailed(false);
    setView({ scale: 1, x: 0, y: 0 });
  }, [src]);

  function begin(event: React.PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 1) {
      gesture.current = { ...gesture.current, startX: event.clientX, startY: event.clientY, originX: view.x, originY: view.y, scale: view.scale };
    } else if (pointers.current.size === 2) {
      const [first, second] = [...pointers.current.values()];
      gesture.current.distance = Math.hypot(second.x - first.x, second.y - first.y);
      gesture.current.scale = view.scale;
    }
  }

  function move(event: React.PointerEvent<HTMLDivElement>) {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size >= 2) {
      const [first, second] = [...pointers.current.values()];
      const distance = Math.hypot(second.x - first.x, second.y - first.y);
      if (gesture.current.distance) setView((current) => ({ ...current, scale: clampScale(gesture.current.scale * distance / gesture.current.distance) }));
    } else if (view.scale > 1) {
      setView((current) => ({ ...current, x: gesture.current.originX + event.clientX - gesture.current.startX, y: gesture.current.originY + event.clientY - gesture.current.startY }));
    }
  }

  function end(event: React.PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId);
    const remaining = [...pointers.current.values()][0];
    if (remaining) gesture.current = { ...gesture.current, startX: remaining.x, startY: remaining.y, originX: view.x, originY: view.y, scale: view.scale, distance: 0 };
  }

  function zoom(delta: number) {
    setView((current) => {
      const scale = clampScale(current.scale + delta);
      return scale === 1 ? { scale, x: 0, y: 0 } : { ...current, scale };
    });
  }

  return <div className="mobile-zoom-viewer">
    <div
      className={`mobile-zoom-stage ${view.scale > 1 ? "zoomed" : ""}`}
      data-mobile-zoom
      role="img"
      aria-label={`Zoomable screenshot: ${alt}`}
      onPointerDown={begin}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => setView((current) => current.scale > 1 ? { scale: 1, x: 0, y: 0 } : { ...current, scale: 2 })}
      onWheel={(event) => { event.preventDefault(); zoom(event.deltaY < 0 ? .25 : -.25); }}
    >{loadFailed
      ? <div className="mobile-screenshot-error" role="alert"><ImageIcon /><strong>Screenshot could not be decoded</strong><small>The exact link exists, but Safari could not display this image file.</small></div>
      : <img src={src} alt={alt} draggable={false} onError={() => setLoadFailed(true)} style={{ transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})` }} />}</div>
    <div className="mobile-zoom-controls" aria-label="Screenshot zoom controls">
      <button onClick={() => zoom(-.5)} aria-label="Zoom screenshot out"><Minus /></button>
      <output>{Math.round(view.scale * 100)}%</output>
      <button onClick={() => zoom(.5)} aria-label="Zoom screenshot in"><Plus /></button>
      <button onClick={() => setView({ scale: 1, x: 0, y: 0 })}>Fit</button>
      <button onClick={onOpen} aria-label="Open screenshot in a separate viewer"><ExternalLink /></button>
    </div>
  </div>;
}

function renderInlineTags(text: string, keyPrefix: string, onTagClick?: (tag: string) => void): React.ReactNode[] {
  const pattern = /⟦(?:\/?T|R)\d+⟧/g;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > cursor) nodes.push(...renderVisibleText(text.slice(cursor, start), `${keyPrefix}-text-${cursor}`));
    nodes.push(<span
      className="mobile-inline-tag"
      key={`${keyPrefix}-${start}`}
      title={match[0]}
      role={onTagClick ? "button" : undefined}
      tabIndex={onTagClick ? 0 : undefined}
      onClick={() => onTagClick?.(match[0])}
      onKeyDown={(event) => { if (onTagClick && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onTagClick(match[0]); } }}
    >{tagDisplayLabel(match[0])}</span>);
    cursor = start + match[0].length;
  }
  if (cursor < text.length) nodes.push(...renderVisibleText(text.slice(cursor), `${keyPrefix}-text-${cursor}`));
  return nodes;
}

function renderVisibleText(text: string, keyPrefix: string): React.ReactNode[] {
  const output: React.ReactNode[] = [];
  let sequence = 0;
  let cursor = 0;
  for (const match of text.matchAll(/[ \t\n]/gu)) {
    const start = match.index ?? 0;
    if (start > cursor) output.push(<span className="mobile-word-run" key={`${keyPrefix}-word-${sequence++}`}>{renderNonbreakingRun(text.slice(cursor, start), `${keyPrefix}-nbsp-${sequence}`)}</span>);
    const character = match[0];
    const className = character === " " ? "space" : character === "\t" ? "tab" : character === "\n" ? "break" : "nonbreaking";
    const label = character === " " ? "space" : character === "\t" ? "tab" : character === "\n" ? "line break" : character === "\u202f" ? "narrow non-breaking space" : "non-breaking space";
    output.push(<span className={`mobile-whitespace ${className}`} aria-label={label} key={`${keyPrefix}-space-${sequence++}`}>{character === "\t" ? "→" : character === "\n" ? "↵" : "·"}</span>);
    if (character === "\n") output.push(<br key={`${keyPrefix}-break-${sequence++}`} />);
    cursor = start + character.length;
  }
  if (cursor < text.length) output.push(<span className="mobile-word-run" key={`${keyPrefix}-word-${sequence++}`}>{renderNonbreakingRun(text.slice(cursor), `${keyPrefix}-nbsp-${sequence}`)}</span>);
  return output;
}

function renderNonbreakingRun(text: string, keyPrefix: string) {
  const output: React.ReactNode[] = [];
  let cursor = 0;
  for (const match of text.matchAll(/[\u00a0\u202f]/gu)) {
    const start = match.index ?? 0;
    if (start > cursor) output.push(text.slice(cursor, start));
    const character = match[0];
    output.push(<span className="mobile-whitespace nonbreaking" aria-label={character === "\u202f" ? "narrow non-breaking space" : "non-breaking space"} key={`${keyPrefix}-${start}`}>·</span>);
    cursor = start + 1;
  }
  if (cursor < text.length) output.push(text.slice(cursor));
  return output;
}

function mobileReferencePriority(reference: MobileReferenceFile) {
  const lower = reference.name.toLocaleLowerCase();
  if (lower.endsWith(".pdf")) return 0;
  if (/\.(?:xlsx|xlsm|xls|csv|tsv)$/u.test(lower)) return 1;
  if (/\.(?:docx?|pptx?)$/u.test(lower)) return 2;
  return 3;
}

function mobileReferenceCapability(reference: MobileReferenceFile, references: MobileReferenceFile[]) {
  const lower = reference.name.toLocaleLowerCase();
  if (lower.endsWith(".pdf")) return "native searchable pages";
  if (/\.(?:xlsx|xlsm|xls|ods|csv|tsv)$/u.test(lower)) return "native sheet, rows and cells";
  if (lower.endsWith(".docx")) return portableLayoutPreview(references, reference) ? "browser-rendered Word + staged PDF" : "browser-rendered Word pages";
  if (lower.endsWith(".odt")) return portableLayoutPreview(references, reference) ? "native formatted ODT + staged PDF" : "native formatted OpenDocument";
  if (/\.(?:doc|rtf|ppt|pptx|odp)$/u.test(lower)) return portableLayoutPreview(references, reference) ? "page-faithful staged view" : "searchable local preview";
  if (/\.(?:txt|md|html?)$/u.test(lower)) return "native searchable text";
  return "local preview";
}

function buildTagInventory(source: string, target: string) {
  const sourceTags = source.match(/⟦(?:\/?T|R)\d+⟧/g) ?? [];
  const available = new Map<string, number>();
  for (const tag of target.match(/⟦(?:\/?T|R)\d+⟧/g) ?? []) available.set(tag, (available.get(tag) ?? 0) + 1);
  return sourceTags.map((tag) => {
    const count = available.get(tag) ?? 0;
    if (count) available.set(tag, count - 1);
    return { tag, present: count > 0 };
  });
}

function usefulMobileReferenceCells(cells: Array<{ column?: string; coordinate?: string; value?: string; language?: string }>) {
  return cells
    .map((cell, index) => ({ cell, index, rank: mobileReferenceRank(cell.column ?? "") }))
    .filter((item): item is typeof item & { rank: number } => item.rank != null && Boolean(item.cell.value?.trim()))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map((item) => item.cell);
}

function mobileReferenceRank(header: string): number | null {
  const key = header.normalize("NFKC").toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
  if (key === "newvus" || /newenglish|newsource/.test(key)) return 10;
  if (key === "vus" || /englishold|previousenglish|sourceenglish|enus|valuesen/.test(key)) return 20;
  if (key === "vfr" || /french|frfr|valuesfr/.test(key)) return 30;
  if (/vzhrcn|zhcn|chinese|中文/.test(key)) return 40;
  if (/revisereason|reasonforchange|changereason/.test(key)) return 50;
  if (/scopeofchange|contextnote|comment|note/.test(key)) return 60;
  if (/stringdescription|description|补齐描述/.test(key)) return 70;
  if (/screenshot|screenimage|screenref/.test(key)) return 80;
  if (/^stringid$/.test(key)) return 90;
  if (/^requireid$/.test(key)) return 91;
  if (/^(numberid|dictid|uid)$/.test(key)) return 92;
  if (/app名称|appname|applicationname/.test(key)) return 93;
  if (/订单号|ordernumber|workorder/.test(key)) return 94;
  return null;
}

function mobileReferenceLabel(header: string) {
  const key = header.normalize("NFKC").toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
  if (key === "newvus" || /newenglish|newsource/.test(key)) return "English — current";
  if (key === "vus" || /englishold|previousenglish|sourceenglish|enus|valuesen/.test(key)) return "English — source";
  if (key === "vfr" || /french|frfr|valuesfr/.test(key)) return "French";
  if (/vzhrcn|zhcn|chinese|中文/.test(key)) return "Chinese original";
  if (/revisereason|reasonforchange|changereason/.test(key)) return "Revise reason";
  if (/scopeofchange/.test(key)) return "Scope of change";
  if (/stringdescription|description|补齐描述/.test(key)) return "Context";
  return header;
}

function shortFileLabel(filename: string) {
  const base = (filename.split("/").at(-1) || filename).replace(/\.(?:sdlxliff|xliff|xlf)$/i, "");
  const vivoOrder = base.match(/(NewTranslate|ReTranslate)[^A-Z0-9]*(TR\d+)/i);
  if (vivoOrder) return `${/^new/i.test(vivoOrder[1]) ? "New" : "Re"} · …${vivoOrder[2].slice(-5)}`;
  return base;
}

function localeFlag(locale: string) {
  const normalized = locale.replace("_", "-");
  const region = normalized.split("-")[1]?.toUpperCase() || ({ en: "US", fr: "FR" }[normalized.slice(0, 2).toLocaleLowerCase()] ?? "");
  return /^[A-Z]{2}$/.test(region)
    ? String.fromCodePoint(...[...region].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65))
    : normalized;
}

function formatFileSize(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  if (size >= 1024 * 1024 * 1024) return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function mobileStorageLabel(status: MobileStorageStatus | null) {
  if (status?.persisted) return "Protected";
  if (status?.pressure === "insufficient") return "Storage full";
  if (status?.pressure === "low") return "Storage low";
  return status?.supported ? "Best effort" : "Browser save";
}

function mobileStorageCapacity(status: MobileStorageStatus | null) {
  return status?.available_bytes == null ? "check safety" : `${formatFileSize(status.available_bytes)} free`;
}

function mobileStorageDescription(status: MobileStorageStatus | null) {
  if (status?.persisted) return "Browser storage is protected from automatic eviction; tap to recheck.";
  if (status?.pressure === "insufficient") return "Estimated browser capacity is below this job's storage requirement; tap to recheck.";
  if (status?.pressure === "low") return "Browser storage is running low; tap to request persistence and recheck.";
  return "Tap to request persistent browser storage and see whether Safari can protect this job from eviction.";
}

function mobileDeliveryBaseName(bundle: MobileJobBundle) {
  const configured = bundle.delivery.package_base_name?.trim();
  if (configured) return configured.replace(/[^A-Za-z0-9._-]+/g, "_");
  const combined = `${bundle.project.name} ${bundle.project.id}`;
  const identifier = combined.match(/\b(L001\d{10}|TO\d{12}|TR\d{11})\b/i)?.[1];
  const client = bundle.project.client.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "").toLocaleLowerCase() || "translation";
  return [client, identifier, bundle.project.target_locale].filter(Boolean).join("_").replace(/[^A-Za-z0-9._-]+/g, "_");
}

function portableReferenceBytes(reference: MobileReferenceFile) {
  const raw = atob(reference.content_base64);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

function safeLocalGet(key: string) {
  try { return window.localStorage.getItem(key); }
  catch { return null; }
}

function safeLocalSet(key: string, value: string) {
  try { window.localStorage.setItem(key, value); return true; }
  catch { return false; }
}

function safeLocalRemove(key: string) {
  try { window.localStorage.removeItem(key); }
  catch { /* Storage can be unavailable in hardened/private browser modes. */ }
}

function shareWasCancelled(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}
